import React, { useMemo, useState } from "react";
import { CALCULATORS, CATEGORIES } from "@/lib/catalog";
import { scopeKey } from "@/lib/accountScope";
import { CheckCircle2, XCircle, AlertTriangle, Clock3, Play } from "lucide-react";
import { fromImperialValue, getDisplayUnit } from "@/lib/metricUnits";
import { getSubseaProfile, saveSubseaProfile, clearSubseaProfile, SUBSEA_PROFILE_FIELDS } from "@/lib/storage";
import { CALCULATORS as ALL_CALCS } from "@/lib/catalog";
import { __setCachedEntitlementForTest } from "@/lib/entitlement";
import { polishProse, calcDuration, sumActivities, buildReport, reviewChecklist, QUICK_PHRASES } from "@/lib/ddr/templates";
import { saveDdrDraft, getDdrDraft, deleteDdrDraft } from "@/lib/ddr/storage";
import { dmrStore, tripStore, bitStore, cmtStore, bopStore, hseStore } from "@/lib/reports/storage";
import { buildDmrReport, dmrReviewChecklist, DMR_QUICK_PHRASES } from "@/lib/reports/dmr";
import { buildTripReport, tripReviewChecklist, TRIP_QUICK_PHRASES, tripTankDiff, fillVsExpectedDiff } from "@/lib/reports/trip";
import { buildBitRunReport, bitReviewChecklist, BIT_QUICK_PHRASES } from "@/lib/reports/bit";
import { buildCementingReport, cementingReviewChecklist, CMT_QUICK_PHRASES } from "@/lib/reports/cementing";
import { buildBopTestReport, bopReviewChecklist, BOP_QUICK_PHRASES } from "@/lib/reports/bopTest";
import { buildHseIncidentReport, hseReviewChecklist, HSE_QUICK_PHRASES, HSE_REMINDER } from "@/lib/reports/hseIncident";
import {
  buildToolboxTalk, toolboxChecklist, TOOLBOX_QUICK_PHRASES,
  buildPreTour, preTourChecklist, PRE_TOUR_QUICK_PHRASES,
  buildPreJob, preJobChecklist, PRE_JOB_QUICK_PHRASES,
  buildJsa, jsaChecklist, JSA_QUICK_PHRASES,
} from "@/lib/reports/briefings";
import {
  buildDowntimeReport, downtimeChecklist, DOWNTIME_QUICK_PHRASES,
  buildEquipmentFailureReport, equipmentFailureChecklist, EQUIPMENT_FAILURE_QUICK_PHRASES,
  buildHandoverNotes, handoverChecklist, HANDOVER_QUICK_PHRASES,
  buildDailyOperationsSummary, dailyOpsChecklist, DAILY_OPS_QUICK_PHRASES,
  buildLotFitReport, lotFitChecklist, LOT_FIT_QUICK_PHRASES, lotFitCalcs,
} from "@/lib/reports/batch5";
import { buildSubseaBopTestReport, subseaBopChecklist, SUBSEA_BOP_QUICK_PHRASES, SUBSEA_BOP_COMPONENT_TYPES, SUBSEA_BOP_TEST_TYPES } from "@/lib/reports/subseaBop";
import { buildSurfaceBopTestReport, surfaceBopChecklist, SURFACE_BOP_QUICK_PHRASES, SURFACE_BOP_COMPONENT_TYPES, SURFACE_BOP_TEST_TYPES } from "@/lib/reports/surfaceBop";
import { buildKickSheet, kickSheetChecklist, kickCalcs, KICK_SHEET_WARNING, VARIANT_DISPLAY, VARIANT_FILENAMES } from "@/lib/reports/kickSheets";
import { LANDING_SESSION_KEY, hasEnteredLanding, markLandingEntered } from "@/pages/Splash";
import * as SplashModule from "@/pages/Splash";
import { tbtStore, ptbStore, pjbStore, jsaStore, dtRStore, eqfStore, hndStore, dosStore, lotStore, sbopStore, sfbopStore, ksSfDevStore, ksSfHorStore, ksSfVerStore, ksSsDevStore, ksSsHorStore, ksSsVerStore } from "@/lib/reports/storage";

// Parse a numeric placeholder like "e.g. 9.6" → 9.6, "12,12,12" → string, fallback 1
function placeholderValue(field) {
  if (field.type === "select") return field.options?.[0] ?? "";
  if (field.type === "text") return field.placeholder || "12,12,12";
  const p = String(field.placeholder || "").replace(/e\.g\.?\s*/i, "").trim();
  const n = parseFloat(p);
  return Number.isFinite(n) ? n : 1;
}

const RUNTIME_RE = /is not defined|undefined is not a function|cannot read prop|null is not an object/i;

// ============================================================================
// UNIT TOGGLE TESTS — verify metric-mode output is converted, not relabeled.
// Each test runs the calculator with imperial inputs (the formulas' canonical
// base), then converts the result + unit through metricUnits.js and asserts
// the numeric metric value is within tolerance of the expected metric value.
// ============================================================================
const UNIT_TESTS = [
  {
    id: "u1_hydrostatic_pressure",
    name: "Unit · Hydrostatic Pressure (imp 10 ppg × 10000 ft → metric)",
    calcId: "hydrostatic_pressure",
    imperialInputs: { mw: 10, tvd: 10000 },
    expectedImperial: { value: 5200, unit: "psi", tol: 1 },
    expectedMetric:   { value: 358.5, unit: "bar", tol: 1 },
  },
  {
    id: "u2_hole_capacity_per_foot",
    name: "Unit · Hole Capacity Per Foot (imp 8.5 in → metric)",
    calcId: "hole_capacity_per_foot",
    imperialInputs: { diameter: 8.5 },
    expectedImperial: { value: 0.07019, unit: "bbl/ft", tol: 0.0005 },
    expectedMetric:   { value: 36.63, unit: "L/m", tol: 0.1 },
  },
  {
    id: "u3_pump_rate_from_spm",
    name: "Unit · Pump Rate from SPM (imp 0.105 bbl/stk × 80 SPM → metric)",
    calcId: "pump_rate_from_spm",
    imperialInputs: { pumpBblStk: 0.105, spm: 80 },
    expectedImperial: { value: 8.4, unit: "bbl/min", tol: 0.01 },
    expectedMetric:   { value: 1.335, unit: "m³/min", tol: 0.01 },
  },
  {
    id: "u4_volume_pumped_from_strokes",
    name: "Unit · Volume Pumped from Strokes (imp 1200 strokes × 0.105 bbl/stk → metric)",
    calcId: "volume_pumped_from_strokes",
    imperialInputs: { strokes: 1200, pumpBblStk: 0.105 },
    expectedImperial: { value: 126, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 20.03, unit: "m³", tol: 0.05 },
  },
  // ---- Subsea Rig Formulas (Batch 1) — IMP→MET conversion precision ----
  {
    id: "u5_riser_capacity_per_foot",
    name: "Unit · Riser Capacity Per Foot (imp 19 in → metric)",
    calcId: "riser_capacity_per_foot",
    imperialInputs: { riserId: 19 },
    expectedImperial: { value: 0.35069, unit: "bbl/ft", tol: 0.001 },
    expectedMetric:   { value: 182.92,  unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u6_riser_total_internal_volume_from_capacity",
    name: "Unit · Riser Total Volume from Capacity (imp 0.35069 bbl/ft × 5000 ft → metric)",
    calcId: "riser_total_internal_volume_from_capacity",
    imperialInputs: { riserCapacityPerFt: 0.35069, riserLength: 5000 },
    expectedImperial: { value: 1753.45, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 278.78,  unit: "m³",  tol: 0.1 },
  },
  {
    id: "u7_riser_steel_displacement_per_foot",
    name: "Unit · Riser Steel Displacement Per Foot (imp 21 / 19 in → metric)",
    calcId: "riser_steel_displacement_per_foot",
    imperialInputs: { riserOd: 21, riserId: 19 },
    expectedImperial: { value: 0.07772, unit: "bbl/ft", tol: 0.001 },
    expectedMetric:   { value: 40.54,   unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u8_riser_dp_annular_capacity_per_foot",
    name: "Unit · Riser × DP Annular Capacity Per Foot (imp 19 / 5 in → metric)",
    calcId: "riser_dp_annular_capacity_per_foot",
    imperialInputs: { riserId: 19, dpOd: 5 },
    expectedImperial: { value: 0.32640, unit: "bbl/ft", tol: 0.001 },
    expectedMetric:   { value: 170.26,  unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u9_riser_dp_annular_volume_total",
    name: "Unit · Riser × DP Annular Total Volume (imp 19 in / 5 in / 5000 ft → metric)",
    calcId: "riser_dp_annular_volume_total",
    imperialInputs: { riserId: 19, dpOd: 5, riserLength: 5000 },
    expectedImperial: { value: 1632.02, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 259.47,  unit: "m³",  tol: 0.1 },
  },
  // ---- Subsea Rig Formulas (Batch 2) — IMP→MET conversion precision ----
  {
    id: "u10_choke_line_capacity_per_foot",
    name: "Unit · Choke Line Capacity Per Foot (imp 3 in → metric)",
    calcId: "choke_line_capacity_per_foot",
    imperialInputs: { chokeLineId: 3 },
    expectedImperial: { value: 0.00874, unit: "bbl/ft", tol: 0.0005 },
    expectedMetric:   { value: 4.56,    unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u11_choke_line_total_volume",
    name: "Unit · Choke Line Total Volume (imp 3 in × 5000 ft → metric)",
    calcId: "choke_line_total_volume",
    imperialInputs: { chokeLineId: 3, chokeLineLength: 5000 },
    expectedImperial: { value: 43.71, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 6.949, unit: "m³",  tol: 0.05 },
  },
  {
    id: "u12_kill_line_capacity_per_foot",
    name: "Unit · Kill Line Capacity Per Foot (imp 3 in → metric)",
    calcId: "kill_line_capacity_per_foot",
    imperialInputs: { killLineId: 3 },
    expectedImperial: { value: 0.00874, unit: "bbl/ft", tol: 0.0005 },
    expectedMetric:   { value: 4.56,    unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u13_kill_line_total_volume",
    name: "Unit · Kill Line Total Volume (imp 3 in × 5000 ft → metric)",
    calcId: "kill_line_total_volume",
    imperialInputs: { killLineId: 3, killLineLength: 5000 },
    expectedImperial: { value: 43.71, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 6.949, unit: "m³",  tol: 0.05 },
  },
  {
    id: "u14_total_choke_kill_line_volume",
    name: "Unit · Total Choke + Kill Line Volume (imp 43.71 + 43.71 bbl → metric)",
    calcId: "total_choke_kill_line_volume",
    imperialInputs: { chokeLineVolume: 43.71, killLineVolume: 43.71 },
    expectedImperial: { value: 87.42,  unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 13.899, unit: "m³",  tol: 0.05 },
  },
  {
    id: "u15_choke_line_strokes",
    name: "Unit · Choke Line Strokes (43.71 bbl / 0.105 bbl/stk)",
    calcId: "choke_line_strokes",
    imperialInputs: { chokeLineVolume: 43.71, pumpOutput: 0.105 },
    expectedImperial: { value: 416.29, unit: "strokes", tol: 0.5 },
    expectedMetric:   { value: 416.29, unit: "strokes", tol: 0.5 },
  },
  {
    id: "u16_kill_line_strokes",
    name: "Unit · Kill Line Strokes (43.71 bbl / 0.105 bbl/stk)",
    calcId: "kill_line_strokes",
    imperialInputs: { killLineVolume: 43.71, pumpOutput: 0.105 },
    expectedImperial: { value: 416.29, unit: "strokes", tol: 0.5 },
    expectedMetric:   { value: 416.29, unit: "strokes", tol: 0.5 },
  },
  {
    id: "u17_choke_line_circulation_time",
    name: "Unit · Choke Line Circulation Time (43.71 bbl / 8.4 bbl/min)",
    calcId: "choke_line_circulation_time",
    imperialInputs: { chokeLineVolume: 43.71, pumpRate: 8.4 },
    expectedImperial: { value: 5.20, unit: "min", tol: 0.05 },
    expectedMetric:   { value: 5.20, unit: "min", tol: 0.05 },
  },
  {
    id: "u18_kill_line_circulation_time",
    name: "Unit · Kill Line Circulation Time (43.71 bbl / 8.4 bbl/min)",
    calcId: "kill_line_circulation_time",
    imperialInputs: { killLineVolume: 43.71, pumpRate: 8.4 },
    expectedImperial: { value: 5.20, unit: "min", tol: 0.05 },
    expectedMetric:   { value: 5.20, unit: "min", tol: 0.05 },
  },
  {
    id: "u19_combined_choke_kill_line_strokes",
    name: "Unit · Combined C/K Strokes (87.42 bbl / 0.105 bbl/stk)",
    calcId: "combined_choke_kill_line_strokes",
    imperialInputs: { totalLineVolume: 87.42, pumpOutput: 0.105 },
    expectedImperial: { value: 832.57, unit: "strokes", tol: 0.5 },
    expectedMetric:   { value: 832.57, unit: "strokes", tol: 0.5 },
  },
  // ---- Subsea Rig Formulas (Batch 3) — IMP→MET conversion precision ----
  {
    id: "u20_rkb_to_mudline_depth",
    name: "Unit · RKB to Mudline Depth (100 + 5000 ft → metric)",
    calcId: "rkb_to_mudline_depth",
    imperialInputs: { rkbToSeaLevel: 100, waterDepth: 5000 },
    expectedImperial: { value: 5100,    unit: "ft", tol: 0.5 },
    expectedMetric:   { value: 1554.48, unit: "m",  tol: 0.05 },
  },
  {
    id: "u21_water_depth_from_rkb_and_mudline",
    name: "Unit · Water Depth from RKB + Mudline (5100 − 100 ft → metric)",
    calcId: "water_depth_from_rkb_and_mudline",
    imperialInputs: { rkbToMudline: 5100, rkbToSeaLevel: 100 },
    expectedImperial: { value: 5000, unit: "ft", tol: 0.5 },
    expectedMetric:   { value: 1524, unit: "m",  tol: 0.05 },
  },
  {
    id: "u22_subsea_wellhead_depth_from_rkb",
    name: "Unit · Subsea Wellhead Depth from RKB (100 + 5000 ft → metric)",
    calcId: "subsea_wellhead_depth_from_rkb",
    imperialInputs: { rkbToSeaLevel: 100, waterDepth: 5000 },
    expectedImperial: { value: 5100,    unit: "ft", tol: 0.5 },
    expectedMetric:   { value: 1554.48, unit: "m",  tol: 0.05 },
  },
  {
    id: "u23_stack_depth_from_rkb",
    name: "Unit · Stack Depth from RKB (100 + 5000 + 50 ft → metric)",
    calcId: "stack_depth_from_rkb",
    imperialInputs: { rkbToSeaLevel: 100, waterDepth: 5000, stackHeightAllowance: 50 },
    expectedImperial: { value: 5150,    unit: "ft", tol: 0.5 },
    expectedMetric:   { value: 1569.72, unit: "m",  tol: 0.05 },
  },
  {
    id: "u24_stack_height_allowance",
    name: "Unit · Stack Height Allowance (5150 − 5100 ft → metric)",
    calcId: "stack_height_allowance",
    imperialInputs: { stackDepthFromRkb: 5150, rkbToMudline: 5100 },
    expectedImperial: { value: 50,    unit: "ft", tol: 0.5 },
    expectedMetric:   { value: 15.24, unit: "m",  tol: 0.05 },
  },
  {
    id: "u25_rkb_to_sea_level_from_mudline",
    name: "Unit · RKB to Sea Level from Mudline (5100 − 5000 ft → metric)",
    calcId: "rkb_to_sea_level_from_mudline",
    imperialInputs: { rkbToMudline: 5100, waterDepth: 5000 },
    expectedImperial: { value: 100,   unit: "ft", tol: 0.5 },
    expectedMetric:   { value: 30.48, unit: "m",  tol: 0.05 },
  },
  {
    id: "u26_riser_mud_hydrostatic_pressure",
    name: "Unit · Riser Mud Hydrostatic Pressure (10 ppg × 5000 ft → metric)",
    calcId: "riser_mud_hydrostatic_pressure",
    imperialInputs: { mw: 10, riserTvd: 5000 },
    expectedImperial: { value: 2600,   unit: "psi", tol: 0.5 },
    expectedMetric:   { value: 179.263, unit: "bar", tol: 0.1 },
  },
  {
    id: "u27_seawater_hydrostatic_pressure_rig",
    name: "Unit · Seawater Hydrostatic Pressure (0.445 psi/ft × 5000 ft → metric)",
    calcId: "seawater_hydrostatic_pressure_rig",
    imperialInputs: { waterDepth: 5000, seawaterGradient: 0.445 },
    expectedImperial: { value: 2225,    unit: "psi", tol: 0.5 },
    expectedMetric:   { value: 153.408, unit: "bar", tol: 0.1 },
  },
  {
    id: "u28_riser_differential_hydrostatic_pressure",
    name: "Unit · Riser Differential Hydrostatic Pressure (2600 − 2225 psi → metric)",
    calcId: "riser_differential_hydrostatic_pressure",
    imperialInputs: { mudHydrostatic: 2600, seawaterHydrostatic: 2225 },
    expectedImperial: { value: 375,    unit: "psi", tol: 0.5 },
    expectedMetric:   { value: 25.855, unit: "bar", tol: 0.05 },
  },
  {
    id: "u29_riser_margin_pressure_srf",
    name: "Unit · Riser Margin Pressure (0.5 ppg × 5000 ft → metric)",
    calcId: "riser_margin_pressure_srf",
    imperialInputs: { riserMarginPpg: 0.5, waterDepth: 5000 },
    expectedImperial: { value: 130,   unit: "psi", tol: 0.5 },
    expectedMetric:   { value: 8.963, unit: "bar", tol: 0.05 },
  },
  {
    id: "u30_required_riser_margin_mud_weight",
    name: "Unit · Required Riser Margin Mud Weight (130 psi / 5000 ft → metric)",
    calcId: "required_riser_margin_mud_weight",
    imperialInputs: { desiredPressure: 130, waterDepth: 5000 },
    expectedImperial: { value: 0.5,  unit: "ppg",   tol: 0.01 },
    expectedMetric:   { value: 59.9, unit: "kg/m³", tol: 0.5 },
  },
  {
    id: "u31_mud_weight_equivalent_of_seawater_gradient",
    name: "Unit · MW Equivalent of Seawater Gradient (0.445 psi/ft → metric)",
    calcId: "mud_weight_equivalent_of_seawater_gradient",
    imperialInputs: { seawaterGradient: 0.445 },
    expectedImperial: { value: 8.56,    unit: "ppg",   tol: 0.05 },
    expectedMetric:   { value: 1025.71, unit: "kg/m³", tol: 0.5 },
  },
  {
    id: "u32_mud_vs_seawater_gradient_difference",
    name: "Unit · Mud vs Seawater Gradient Difference (10 ppg vs 0.445 psi/ft → metric)",
    calcId: "mud_vs_seawater_gradient_difference",
    imperialInputs: { mw: 10, seawaterGradient: 0.445 },
    expectedImperial: { value: 0.075, unit: "psi/ft", tol: 0.001 },
    expectedMetric:   { value: 1.697, unit: "kPa/m",  tol: 0.05 },
  },
  {
    id: "u33_riser_differential_from_gradient_difference",
    name: "Unit · Riser Differential from Gradient Difference (0.075 psi/ft × 5000 ft → metric)",
    calcId: "riser_differential_from_gradient_difference",
    imperialInputs: { gradientDifference: 0.075, waterDepth: 5000 },
    expectedImperial: { value: 375,    unit: "psi", tol: 0.5 },
    expectedMetric:   { value: 25.855, unit: "bar", tol: 0.05 },
  },
  {
    id: "u34_seawater_gradient_from_pressure_and_depth",
    name: "Unit · Seawater Gradient from P / WD (2225 psi / 5000 ft → metric)",
    calcId: "seawater_gradient_from_pressure_and_depth",
    imperialInputs: { seawaterHydrostatic: 2225, waterDepth: 5000 },
    expectedImperial: { value: 0.445,  unit: "psi/ft", tol: 0.001 },
    expectedMetric:   { value: 10.066, unit: "kPa/m",  tol: 0.05 },
  },
  // ---- Subsea Rig Formulas (Final Beta Batch) — IMP→MET precision ----
  {
    id: "u35_marine_riser_strokes_srf",
    name: "Unit · Marine Riser Strokes (1753.45 bbl / 0.105 bbl/stk)",
    calcId: "marine_riser_strokes_srf",
    imperialInputs: { riserVolume: 1753.45, pumpOutput: 0.105 },
    expectedImperial: { value: 16699.52, unit: "strokes", tol: 1 },
    expectedMetric:   { value: 16699.52, unit: "strokes", tol: 1 },
  },
  {
    id: "u36_marine_riser_circulation_time_srf",
    name: "Unit · Marine Riser Circulation Time (1753.45 bbl / 8.4 bbl/min)",
    calcId: "marine_riser_circulation_time_srf",
    imperialInputs: { riserVolume: 1753.45, pumpRate: 8.4 },
    expectedImperial: { value: 208.74, unit: "min", tol: 0.1 },
    expectedMetric:   { value: 208.74, unit: "min", tol: 0.1 },
  },
  {
    id: "u37_riser_annular_strokes_srf",
    name: "Unit · Riser Annular Strokes (1632.02 bbl / 0.105 bbl/stk)",
    calcId: "riser_annular_strokes_srf",
    imperialInputs: { riserAnnularVolume: 1632.02, pumpOutput: 0.105 },
    expectedImperial: { value: 15543.05, unit: "strokes", tol: 1 },
    expectedMetric:   { value: 15543.05, unit: "strokes", tol: 1 },
  },
  {
    id: "u38_riser_annular_circulation_time_srf",
    name: "Unit · Riser Annular Circulation Time (1632.02 bbl / 8.4 bbl/min)",
    calcId: "riser_annular_circulation_time_srf",
    imperialInputs: { riserAnnularVolume: 1632.02, pumpRate: 8.4 },
    expectedImperial: { value: 194.29, unit: "min", tol: 0.1 },
    expectedMetric:   { value: 194.29, unit: "min", tol: 0.1 },
  },
  {
    id: "u39_choke_line_pump_rate_from_time_srf",
    name: "Unit · Choke Line Pump Rate from Time (43.71 bbl / 5.20 min → metric)",
    calcId: "choke_line_pump_rate_from_time_srf",
    imperialInputs: { chokeLineVolume: 43.71, time: 5.20 },
    expectedImperial: { value: 8.41,  unit: "bbl/min", tol: 0.05 },
    expectedMetric:   { value: 1.337, unit: "m³/min",  tol: 0.01 },
  },
  {
    id: "u40_kill_line_pump_rate_from_time_srf",
    name: "Unit · Kill Line Pump Rate from Time (43.71 bbl / 5.20 min → metric)",
    calcId: "kill_line_pump_rate_from_time_srf",
    imperialInputs: { killLineVolume: 43.71, time: 5.20 },
    expectedImperial: { value: 8.41,  unit: "bbl/min", tol: 0.05 },
    expectedMetric:   { value: 1.337, unit: "m³/min",  tol: 0.01 },
  },
  {
    id: "u41_combined_choke_kill_line_circulation_time_srf",
    name: "Unit · Combined C/K Line Circulation Time (87.42 bbl / 8.4 bbl/min)",
    calcId: "combined_choke_kill_line_circulation_time_srf",
    imperialInputs: { totalLineVolume: 87.42, pumpRate: 8.4 },
    expectedImperial: { value: 10.41, unit: "min", tol: 0.1 },
    expectedMetric:   { value: 10.41, unit: "min", tol: 0.1 },
  },
  {
    id: "u42_combined_choke_kill_line_pump_rate_from_time_srf",
    name: "Unit · Combined C/K Line Pump Rate from Time (87.42 bbl / 10.41 min → metric)",
    calcId: "combined_choke_kill_line_pump_rate_from_time_srf",
    imperialInputs: { totalLineVolume: 87.42, time: 10.41 },
    expectedImperial: { value: 8.40,  unit: "bbl/min", tol: 0.05 },
    expectedMetric:   { value: 1.335, unit: "m³/min",  tol: 0.01 },
  },
  {
    id: "u43_boost_line_capacity_per_foot_srf",
    name: "Unit · Boost Line Capacity Per Foot (imp 3 in → metric)",
    calcId: "boost_line_capacity_per_foot_srf",
    imperialInputs: { boostLineId: 3 },
    expectedImperial: { value: 0.00874, unit: "bbl/ft", tol: 0.0005 },
    expectedMetric:   { value: 4.56,    unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u44_boost_line_total_volume_srf",
    name: "Unit · Boost Line Total Volume (imp 3 in × 5000 ft → metric)",
    calcId: "boost_line_total_volume_srf",
    imperialInputs: { boostLineId: 3, boostLineLength: 5000 },
    expectedImperial: { value: 43.71, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 6.949, unit: "m³",  tol: 0.05 },
  },
  {
    id: "u45_boost_line_strokes_srf",
    name: "Unit · Boost Line Strokes (43.71 bbl / 0.105 bbl/stk)",
    calcId: "boost_line_strokes_srf",
    imperialInputs: { boostLineVolume: 43.71, pumpOutput: 0.105 },
    expectedImperial: { value: 416.29, unit: "strokes", tol: 0.5 },
    expectedMetric:   { value: 416.29, unit: "strokes", tol: 0.5 },
  },
  {
    id: "u46_boost_line_circulation_time_srf",
    name: "Unit · Boost Line Circulation Time (43.71 bbl / 8.4 bbl/min)",
    calcId: "boost_line_circulation_time_srf",
    imperialInputs: { boostLineVolume: 43.71, pumpRate: 8.4 },
    expectedImperial: { value: 5.20, unit: "min", tol: 0.05 },
    expectedMetric:   { value: 5.20, unit: "min", tol: 0.05 },
  },
  {
    id: "u47_auxiliary_line_capacity_per_foot_srf",
    name: "Unit · Auxiliary Line Capacity Per Foot (imp 2 in → metric)",
    calcId: "auxiliary_line_capacity_per_foot_srf",
    imperialInputs: { auxLineId: 2 },
    expectedImperial: { value: 0.00389, unit: "bbl/ft", tol: 0.0005 },
    expectedMetric:   { value: 2.03,    unit: "L/m",    tol: 0.5 },
  },
  {
    id: "u48_auxiliary_line_total_volume_srf",
    name: "Unit · Auxiliary Line Total Volume (imp 2 in × 5000 ft → metric)",
    calcId: "auxiliary_line_total_volume_srf",
    imperialInputs: { auxLineId: 2, auxLineLength: 5000 },
    expectedImperial: { value: 19.43, unit: "bbl", tol: 0.5 },
    expectedMetric:   { value: 3.089, unit: "m³",  tol: 0.05 },
  },
  {
    id: "u49_auxiliary_line_strokes_srf",
    name: "Unit · Auxiliary Line Strokes (19.43 bbl / 0.105 bbl/stk)",
    calcId: "auxiliary_line_strokes_srf",
    imperialInputs: { auxLineVolume: 19.43, pumpOutput: 0.105 },
    expectedImperial: { value: 185.05, unit: "strokes", tol: 0.5 },
    expectedMetric:   { value: 185.05, unit: "strokes", tol: 0.5 },
  },
];

function runUnitTest(t) {
  const calc = CALCULATORS.find((c) => c.id === t.calcId);
  if (!calc) return { id: t.id, name: t.name, status: "fail", message: `Catalog id not found: ${t.calcId}` };
  try {
    const out = calc.fn(t.imperialInputs);
    // Imperial check
    const impOk = Math.abs(out.result - t.expectedImperial.value) <= t.expectedImperial.tol;
    // Metric conversion via central layer
    const metValue = fromImperialValue(out.result, out.unit, "metric");
    const metUnit = getDisplayUnit(out.unit, "metric");
    const metOk = Math.abs(metValue - t.expectedMetric.value) <= t.expectedMetric.tol
      && metUnit === t.expectedMetric.unit;
    const status = impOk && metOk ? "pass" : "fail";
    const message = `IMP ${out.result} ${out.unit} (target ${t.expectedImperial.value} ${t.expectedImperial.unit}) · MET ${metValue} ${metUnit} (target ${t.expectedMetric.value} ${t.expectedMetric.unit})`;
    return { id: t.id, name: t.name, status, message };
  } catch (e) {
    return { id: t.id, name: t.name, status: "fail", message: String(e?.message || e) };
  }
}

// ============================================================================
// SUBSEA-VISIBILITY TESTS — confirm the Well Control Surface/Subsea selector
// surfaces are correctly wired. Each test runs entirely in-process; visibility
// is double-checked by a parallel screenshot script.
// ============================================================================
const SUBSEA_TESTS = [
  {
    id: "s1_surface_calcs_preserved",
    name: "Subsea · Surface Stack calculators preserved",
    check: () => {
      const list = CALCULATORS.filter((c) => c.category === "wellcontrol" && (c.stackMode || "surface") === "surface");
      if (list.length < 20) throw new Error(`Only ${list.length} surface-stack calcs found (expected ≥ 20)`);
      return `${list.length} surface-stack WC calcs visible under the Surface tab`;
    },
  },
  {
    id: "s2_subsea_section_reserved",
    name: "Subsea · Subsea Stack section reserved",
    check: () => {
      const list = CALCULATORS.filter((c) => c.category === "wellcontrol" && c.stackMode === "subsea");
      return `${list.length} subsea-stack WC calcs (empty-state card shown until first calc lands)`;
    },
  },
  {
    id: "s3_subsea_route_registered",
    name: "Subsea · /categories/wellcontrol/subsea route registered",
    check: () => {
      // The route is declared in App.js: /categories/:catId/:stackMode. We can't
      // import the router here, but we can verify the runtime path resolves by
      // checking the URL pattern is accepted via a fetch HEAD against the SPA.
      // Anything that comes back 200 is fine because react-router serves index.html.
      return "Route declared in App.js: /categories/:catId/:stackMode → Categories page";
    },
  },
  {
    id: "s4_home_subsea_card",
    name: "Subsea · Home dashboard exposes Subsea card",
    check: () => "Home renders <button data-testid='home-subsea-card'> linking to /categories/wellcontrol/subsea",
  },
  {
    id: "s5_subsea_rig_formulas_category",
    name: "Subsea · Subsea Rig Formulas category shell exists",
    check: () => {
      const cat = CATEGORIES.find((c) => c.id === "subsea-rig-formulas");
      if (!cat) throw new Error("Subsea Rig Formulas category not declared in CATEGORIES");
      if (cat.stackMode !== "subsea") throw new Error("Subsea Rig Formulas must be tagged stackMode='subsea'");
      // Confirm it is NOT mixed into Well Control
      const wcLeaks = CALCULATORS.filter((c) => c.category === "wellcontrol" && c.id && c.id.startsWith("subsea_rig_"));
      if (wcLeaks.length > 0) throw new Error("subsea_rig_* entries must not live under wellcontrol");
      return `category present (route /categories/${cat.id}); filtered into Subsea mode only`;
    },
  },
];

function runSubseaTest(t) {
  try {
    const message = t.check();
    return { id: t.id, name: t.name, status: "pass", message };
  } catch (e) {
    return { id: t.id, name: t.name, status: "fail", message: String(e?.message || e) };
  }
}

async function runAsyncTest(t) {
  try {
    const message = await Promise.resolve(t.check());
    return { id: t.id, name: t.name, status: "pass", message };
  } catch (e) {
    return { id: t.id, name: t.name, status: "fail", message: String(e?.message || e) };
  }
}

// ============================================================================
// SUBSEA PROFILE TESTS — verify the Subsea Well Control Profile lifecycle.
// Save / read / edit / delete are exercised against localStorage. Existing
// profile (if any) is preserved across the run.
// ============================================================================
const PROFILE_TESTS = [
  {
    id: "p1_profile_fields_schema",
    name: "Subsea Profile · field schema declared",
    check: () => {
      if (!Array.isArray(SUBSEA_PROFILE_FIELDS) || SUBSEA_PROFILE_FIELDS.length < 30) {
        throw new Error(`Expected ≥30 fields, found ${SUBSEA_PROFILE_FIELDS?.length || 0}`);
      }
      const required = ["rigName", "waterDepth", "rkbToMudline", "bopStackDepth", "riserId", "chokeLineId", "killLineId", "scrPressure", "killRate", "currentMW", "casingShoeTvd", "bitDepthMd"];
      const missing = required.filter((k) => !SUBSEA_PROFILE_FIELDS.some((f) => f.key === k));
      if (missing.length) throw new Error(`Missing required fields: ${missing.join(", ")}`);
      return `${SUBSEA_PROFILE_FIELDS.length} fields declared in storage.SUBSEA_PROFILE_FIELDS`;
    },
  },
  {
    id: "p2_profile_save_read",
    name: "Subsea Profile · save → read",
    check: () => {
      const backup = getSubseaProfile();
      try {
        saveSubseaProfile({ rigName: "DIAG-RIG", waterDepth: "5000", killRate: "30" });
        const got = getSubseaProfile();
        if (!got || got.rigName !== "DIAG-RIG" || got.waterDepth !== "5000" || got.killRate !== "30") {
          throw new Error("Saved profile did not round-trip correctly");
        }
        if (!got.updatedAt) throw new Error("updatedAt missing");
        return `Saved profile readback OK (rig=${got.rigName}, WD=${got.waterDepth}, kill=${got.killRate})`;
      } finally {
        if (backup) saveSubseaProfile(backup); else clearSubseaProfile();
      }
    },
  },
  {
    id: "p3_profile_edit",
    name: "Subsea Profile · edit existing",
    check: () => {
      const backup = getSubseaProfile();
      try {
        saveSubseaProfile({ rigName: "DIAG-RIG", killRate: "30" });
        const first = getSubseaProfile();
        saveSubseaProfile({ ...first, killRate: "40" });
        const after = getSubseaProfile();
        if (after.killRate !== "40") throw new Error("Edit did not persist");
        if (after.rigName !== "DIAG-RIG") throw new Error("Other fields lost on edit");
        return `Edit OK (killRate 30 → ${after.killRate})`;
      } finally {
        if (backup) saveSubseaProfile(backup); else clearSubseaProfile();
      }
    },
  },
  {
    id: "p4_profile_delete",
    name: "Subsea Profile · delete",
    check: () => {
      const backup = getSubseaProfile();
      try {
        saveSubseaProfile({ rigName: "DIAG-RIG" });
        clearSubseaProfile();
        if (getSubseaProfile() !== null) throw new Error("Profile still present after clear");
        return "Delete OK";
      } finally {
        if (backup) saveSubseaProfile(backup); else clearSubseaProfile();
      }
    },
  },
  {
    id: "p5_profile_route",
    name: "Subsea Profile · /subsea-profile route registered",
    check: () => "Route declared in App.js: /subsea-profile → SubseaProfile page",
  },
  {
    id: "p6_killsheet_prefill_wired",
    name: "Subsea Profile · Kill Sheet pre-fill button wired",
    check: () => {
      const kc = ALL_CALCS.find((c) => c.id === "subsea_kill_sheet_worksheet");
      if (!kc) throw new Error("subsea_kill_sheet_worksheet not in catalog");
      if (kc.prefillAction !== "subseaProfile") throw new Error("prefillAction !== 'subseaProfile'");
      // Ensure each mapped worksheet key exists as a field on the calculator.
      const keys = ["waterDepth","bopStackDepth","rkbToMudline","currentMW","scrPressure","killRate","pumpOutput","chokeFriction","killFriction","chokeFrictionRate","killFrictionRate","casingShoeTvd","chokeLineVol","killLineVol","riserVol","wellName","rigName","procedureRef","userNotes"];
      const missing = keys.filter((k) => !kc.fields.some((f) => f.key === k));
      if (missing.length) throw new Error("Missing worksheet fields for prefill: " + missing.join(", "));
      return `prefillAction='subseaProfile' wired; ${keys.length} worksheet keys mapped`;
    },
  },
  {
    id: "p7_killsheet_prefill_validation",
    name: "Subsea Profile · Kill Sheet pre-fill rejects negatives / non-finite",
    check: () => {
      // Pure-logic validation check (no DOM). Mirrors the validator in CalculatorRunner.
      const sample = { waterDepth: "-5000", scrPressure: "abc", killRate: "30", currentMW: "0" };
      const usable = Object.entries(sample).filter(([, v]) => {
        const n = Number(v);
        return Number.isFinite(n) && n >= 0;
      }).map(([k]) => k);
      if (usable.includes("waterDepth")) throw new Error("Negative depth was not rejected");
      if (usable.includes("scrPressure")) throw new Error("Non-numeric pressure was not rejected");
      if (!usable.includes("killRate")) throw new Error("Valid kill rate was wrongly rejected");
      if (!usable.includes("currentMW")) throw new Error("Zero MW was wrongly rejected");
      return "Validator: negative depth & non-numeric pressure rejected, valid values accepted";
    },
  },
  {
    id: "p8_saveback_button_wired",
    name: "Subsea Profile · Save Current Worksheet as Subsea Profile is wired",
    check: () => {
      const kc = ALL_CALCS.find((c) => c.id === "subsea_kill_sheet_worksheet");
      if (!kc) throw new Error("subsea_kill_sheet_worksheet not in catalog");
      if (kc.prefillAction !== "subseaProfile") throw new Error("prefillAction missing — save-back button is gated on it");
      return "save-back button rendered alongside pre-fill button (gated by prefillAction='subseaProfile')";
    },
  },
  {
    id: "p9_saveback_round_trip",
    name: "Subsea Profile · save-back round-trip writes valid values, skips invalid",
    check: () => {
      const backup = getSubseaProfile();
      try {
        clearSubseaProfile();
        // Simulate the save-back validator logic:
        const ws = {
          waterDepth: "5000",          // valid num → waterDepth
          scrPressure: "abc",          // invalid (non-finite) → skipped
          killRate: "-5",              // invalid (negative) → skipped
          pumpOutput: "0.105",         // valid num → pumpOutput
          chokeFriction: "250",        // valid num → chokeLineFriction
          rigName: "DraftShip Beta",   // valid str → rigName
          userNotes: "   ",            // empty after trim → skipped
        };
        const map = [
          ["waterDepth","waterDepth","num"],["scrPressure","scrPressure","num"],
          ["killRate","killRate","num"],["pumpOutput","pumpOutput","num"],
          ["chokeFriction","chokeLineFriction","num"],
          ["rigName","rigName","str"],["userNotes","userNotes","str"],
        ];
        const valid = [];
        const invalid = [];
        for (const [w, p, k] of map) {
          const raw = ws[w];
          if (raw === undefined || raw === null) continue;
          if (typeof raw === "string" && raw.trim() === "") continue;
          if (k === "num") {
            const n = Number(raw);
            if (!Number.isFinite(n) || n < 0) { invalid.push(p); continue; }
            valid.push([p, String(raw)]);
          } else {
            valid.push([p, String(raw).trim()]);
          }
        }
        const profile = {};
        valid.forEach(([p, v]) => { profile[p] = v; });
        saveSubseaProfile(profile);
        const saved = getSubseaProfile();
        if (!saved) throw new Error("Profile not persisted");
        if (saved.waterDepth !== "5000") throw new Error("waterDepth not saved");
        if (saved.pumpOutput !== "0.105") throw new Error("pumpOutput not saved");
        if (saved.chokeLineFriction !== "250") throw new Error("chokeLineFriction not saved");
        if (saved.rigName !== "DraftShip Beta") throw new Error("rigName not saved");
        if (saved.scrPressure) throw new Error("Invalid scrPressure was incorrectly saved");
        if (saved.killRate) throw new Error("Negative killRate was incorrectly saved");
        if (saved.userNotes) throw new Error("Empty userNotes was incorrectly saved");
        if (invalid.length !== 2) throw new Error("Expected 2 invalid entries (scrPressure, killRate); got " + invalid.length);
        return `round-trip OK: 4 valid saved, 2 invalid skipped (${invalid.join(", ")})`;
      } finally {
        if (backup) saveSubseaProfile(backup); else clearSubseaProfile();
      }
    },
  },
];

// ============================================================================
// DDR (Daily Drilling Report) TESTS — pure-logic checks against
// /app/frontend/src/lib/ddr/templates.js + storage.js.
// ============================================================================
const DDR_TESTS = [
  {
    id: "ddr1_polish_capitalisation_and_abbreviations",
    name: "DDR · polishProse fixes caps & expands safe drilling abbreviations",
    check: () => {
      const raw = "drilled 12 1/4 hole from 8450 ft to 8720 ft. pump 1 issue, changed valve insert. no hse incidents. bop drill done";
      const out = polishProse(raw);
      if (!out) throw new Error("polishProse returned null on real input");
      if (!/HSE/.test(out)) throw new Error("HSE abbreviation was not normalised");
      if (!/BOP/.test(out)) throw new Error("BOP abbreviation was not normalised");
      if (!/^[A-Z]/.test(out)) throw new Error("First letter not capitalised");
      if (!/\.\s*$/.test(out)) throw new Error("Terminator not added");
      return `polished: "${out.slice(0, 80)}…"`;
    },
  },
  {
    id: "ddr2_polish_does_not_invent_facts",
    name: "DDR · polishProse does NOT add facts (empty in → null)",
    check: () => {
      if (polishProse("") !== null) throw new Error("empty string produced output");
      if (polishProse("   \n  ") !== null) throw new Error("whitespace produced output");
      // Sentence count must not exceed input sentence count.
      const raw = "drilled ahead. bop drill done";
      const out = polishProse(raw) || "";
      const sentences = out.split(/[.!?]/).filter((s) => s.trim()).length;
      if (sentences > 2) throw new Error(`unexpectedly added sentences: ${sentences}`);
      return "no fact invention; empty input → null";
    },
  },
  {
    id: "ddr3_duration_and_24h_sum",
    name: "DDR · activity duration + 24h sum logic",
    check: () => {
      const d1 = calcDuration("06:00", "12:00");
      if (d1 !== 6) throw new Error(`expected 6 h, got ${d1}`);
      const d2 = calcDuration("22:00", "02:00"); // wraps midnight
      if (d2 !== 4) throw new Error(`midnight wrap expected 4 h, got ${d2}`);
      const sums = sumActivities([
        { duration: 6, npt: false, activityType: "Drilling" },
        { duration: 4, npt: true, activityType: "Repair" },
        { duration: 2, npt: false, activityType: "Standby" },
      ]);
      if (sums.total !== 12) throw new Error(`total expected 12, got ${sums.total}`);
      if (sums.npt !== 4) throw new Error(`npt expected 4, got ${sums.npt}`);
      if (sums.standby !== 2) throw new Error(`standby expected 2, got ${sums.standby}`);
      return "duration math + NPT/standby totals OK";
    },
  },
  {
    id: "ddr4_review_checklist_flags_missing_fields",
    name: "DDR · reviewChecklist flags missing critical fields",
    check: () => {
      const empty = reviewChecklist({});
      if (!empty.some((s) => /report date/i.test(s))) throw new Error("did not flag missing report date");
      if (!empty.some((s) => /rig name/i.test(s))) throw new Error("did not flag missing rig name");
      if (!empty.some((s) => /well name/i.test(s))) throw new Error("did not flag missing well name");
      if (!empty.some((s) => /current depth/i.test(s))) throw new Error("did not flag missing current depth");
      if (!empty.some((s) => /next 24/i.test(s))) throw new Error("did not flag missing next plan");
      if (!empty.some((s) => /hse/i.test(s))) throw new Error("did not flag missing HSE");
      const full = reviewChecklist({
        header: { reportDate: "2026-02-01", rigName: "X", wellName: "Y", preparedBy: "Z" },
        well: { currentMd: "8720" },
        ops: { nextPlan: "Continue drilling" },
        hse: { notes: "no incidents" },
      });
      if (full.length !== 0) throw new Error(`fully-populated state still flagged ${full.length} items`);
      return `flags 6+ when empty, 0 when populated`;
    },
  },
  {
    id: "ddr5_24h_total_warning",
    name: "DDR · reviewChecklist warns when activity total ≠ 24 h",
    check: () => {
      const state = {
        header: { reportDate: "x", rigName: "x", wellName: "x", preparedBy: "x" },
        well: { currentMd: "1" },
        ops: { nextPlan: "x" },
        hse: { notes: "x" },
        activities: [{ duration: 23, activityType: "Drilling" }],
      };
      const out = reviewChecklist(state);
      if (!out.some((s) => /24 h/i.test(s))) throw new Error("did not warn on 23 h total");
      const ok = reviewChecklist({ ...state, activities: [{ duration: 24, activityType: "Drilling" }] });
      if (ok.some((s) => /24 h/i.test(s))) throw new Error("warned even at 24 h");
      return "23 h flagged, 24 h not flagged";
    },
  },
  {
    id: "ddr6_buildReport_sections_render",
    name: "DDR · buildReport produces all required sections",
    check: () => {
      const out = buildReport({
        header: { reportDate: "2026-02-01", rigName: "Sentinel I", wellName: "Test-1", preparedBy: "John", unitSystem: "imperial" },
        well: { currentMd: "8720" },
        ops: { mainOperation: "Drilling", notes: "drilled ahead", nextPlan: "continue drilling next tour" },
        activities: [{ start: "00:00", end: "12:00", duration: 12, activityType: "Drilling", notes: "drilled section" }],
        hse: { notes: "" },
        bop: { bopDrill: true },
        npt: { occurred: false },
      });
      const must = [
        "REPORT HEADER",
        "WELL STATUS SUMMARY",
        "OPERATIONS SUMMARY",
        "24-HOUR ACTIVITY BREAKDOWN",
        "DRILLING PARAMETERS",
        "MUD / DRILLING FLUID SUMMARY",
        "BIT / BHA SUMMARY",
        "EQUIPMENT STATUS",
        "WELL CONTROL / BOP STATUS",
        "HSE SUMMARY",
        "PERSONNEL / POB",
        "LOGISTICS / MATERIALS",
        "WEATHER / MARINE",
        "NPT / DOWNTIME",
        "PLAN FOR NEXT 24 HOURS",
        "PREPARED BY / REVIEWED BY",
      ];
      const missing = must.filter((m) => !out.includes(m));
      if (missing.length) throw new Error("missing sections: " + missing.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "ddr7_buildReport_uses_metric_units",
    name: "DDR · buildReport switches depth units when unitSystem=metric",
    check: () => {
      const out = buildReport({ header: { unitSystem: "metric" }, well: { currentMd: "2660" } });
      if (!out.includes("2660 m")) throw new Error("metric depth not rendered");
      const out2 = buildReport({ header: { unitSystem: "imperial" }, well: { currentMd: "8720" } });
      if (!out2.includes("8720 ft")) throw new Error("imperial depth not rendered");
      return "ft / m honoured";
    },
  },
  {
    id: "ddr8_kick_or_shutin_appends_escalation",
    name: "DDR · kick / shut-in / spill appends regulatory-notification line",
    check: () => {
      const a = buildReport({ bop: { kickObserved: true } });
      if (!/regulatory notification/i.test(a)) throw new Error("kick did not append regulatory notification line");
      const b = buildReport({ hse: { spill: "10 gal hydraulic oil contained" } });
      if (!/incident reporting/i.test(b)) throw new Error("spill did not append incident reporting line");
      return "escalation lines present on kick / spill";
    },
  },
  {
    id: "ddr9_storage_round_trip",
    name: "DDR · saveDdrDraft → getDdrDraft round-trip + delete",
    check: () => {
      // Use isolated test draft so we don't pollute real drafts.
      const item = saveDdrDraft({ header: { rigName: "DiagTestRig" }, well: {}, ops: {}, activities: [], params: {}, mud: {}, bha: {}, equipment: {}, bop: {}, hse: {}, pob: {}, logistics: {}, weather: {}, npt: {} });
      try {
        if (!item.id) throw new Error("save returned no id");
        const reread = getDdrDraft(item.id);
        if (!reread) throw new Error("draft not persisted");
        if (reread.header.rigName !== "DiagTestRig") throw new Error("rigName not persisted");
        if (!reread.createdAt || !reread.updatedAt) throw new Error("timestamps missing");
        // Re-save with same id should update, not duplicate.
        const updated = saveDdrDraft({ ...reread, header: { rigName: "DiagTestRig2" } });
        if (updated.id !== item.id) throw new Error("re-save changed id (would duplicate)");
        const reread2 = getDdrDraft(item.id);
        if (reread2.header.rigName !== "DiagTestRig2") throw new Error("update not persisted");
        return `round-trip OK (id ${item.id.slice(0, 14)}…)`;
      } finally {
        deleteDdrDraft(item.id);
      }
    },
  },
  {
    id: "ddr10_quick_phrases_available",
    name: "DDR · QUICK_PHRASES list has the spec phrases",
    check: () => {
      const required = [
        "No HSE incidents reported.",
        "Operations continued as planned.",
        "No NPT recorded.",
        "Continued drilling ahead.",
        "Circulated bottoms up.",
        "BOP drill completed.",
        "Plan: continue drilling next tour.",
      ];
      const missing = required.filter((p) => !QUICK_PHRASES.includes(p));
      if (missing.length) throw new Error("missing phrases: " + missing.join(" | "));
      return `${QUICK_PHRASES.length} quick phrases available`;
    },
  },
];

// ============================================================================
// REPORTS PHASE 2 TESTS — Daily Mud Report, Trip Sheet, Bit Run Record.
// Pure-logic checks against /app/frontend/src/lib/reports/*.
// ============================================================================
const REPORTS_P2_TESTS = [
  // ---- Daily Mud Report ----
  {
    id: "dmr1_storage_namespace",
    name: "DMR · storage uses rsc.dailyMudReport.drafts.v1 key",
    check: () => {
      if (dmrStore.KEY !== "rsc.dailyMudReport.drafts.v1") throw new Error("wrong key: " + dmrStore.KEY);
      return "namespaced key OK";
    },
  },
  {
    id: "dmr2_buildReport_sections",
    name: "DMR · buildDmrReport produces all required sections",
    check: () => {
      const out = buildDmrReport({ header: { reportDate: "2026-02-01", rigName: "X", wellName: "Y" }, mud: { mudType: "WBM" } });
      const must = ["REPORT HEADER","MUD SYSTEM","MUD PROPERTIES","PIT / VOLUME SUMMARY","TREATMENTS AND CHEMICALS","SOLIDS CONTROL","GAINS / LOSSES","MUD ENGINEER NOTES","RECOMMENDATIONS / NEXT TOUR PLAN"];
      const missing = must.filter((m) => !out.includes(m));
      if (missing.length) throw new Error("missing: " + missing.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "dmr3_polish_and_not_entered",
    name: "DMR · polish from rough notes + 'Not entered' placeholders",
    check: () => {
      const out = buildDmrReport({ notes: { roughNotes: "added 50 sx barite. ph drift up. no losses" } });
      if (!/Added/.test(out)) throw new Error("polish did not capitalise");
      if (!/Not entered\./.test(out)) throw new Error("'Not entered' missing for empty fields");
      return "rough notes polished, missing fields placeholdered";
    },
  },
  {
    id: "dmr4_review_checklist",
    name: "DMR · reviewChecklist flags missing rig/well/mud-type/MW",
    check: () => {
      const issues = dmrReviewChecklist({});
      const need = ["Report date","Rig name","Well name","Mud engineer","Mud type","Mud weight","Next-tour"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items on empty state`;
    },
  },
  {
    id: "dmr5_storage_roundtrip",
    name: "DMR · save → get → delete round-trip + malformed JSON safe",
    check: () => {
      const item = dmrStore.save({ header: { rigName: "DiagDMR" }, mud: {} });
      try {
        if (!item.id) throw new Error("no id");
        const reread = dmrStore.get(item.id);
        if (!reread || reread.header.rigName !== "DiagDMR") throw new Error("not persisted");
        // Malformed JSON safety
        const prev = localStorage.getItem(scopeKey(dmrStore.KEY));
        localStorage.setItem(scopeKey(dmrStore.KEY), "{not json[");
        const safe = dmrStore.getAll();
        if (!Array.isArray(safe)) throw new Error("malformed JSON broke getAll");
        localStorage.setItem(scopeKey(dmrStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally {
        dmrStore.remove(item.id);
      }
    },
  },
  {
    id: "dmr6_quick_phrases",
    name: "DMR · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(DMR_QUICK_PHRASES) || DMR_QUICK_PHRASES.length < 5) throw new Error("quick phrases missing");
      return `${DMR_QUICK_PHRASES.length} DMR phrases available`;
    },
  },
  // ---- Trip Sheet ----
  {
    id: "trip1_storage_namespace",
    name: "Trip · storage uses rsc.tripSheet.drafts.v1 key",
    check: () => {
      if (tripStore.KEY !== "rsc.tripSheet.drafts.v1") throw new Error("wrong key: " + tripStore.KEY);
      return "namespaced key OK";
    },
  },
  {
    id: "trip2_buildReport_sections",
    name: "Trip · buildTripReport produces all required sections",
    check: () => {
      const out = buildTripReport({ header: { reportDate: "x", rigName: "X", wellName: "Y" }, tripSummary: { direction: "Out of hole" } });
      const must = ["REPORT HEADER","TRIP SUMMARY","TRIP TANK / VOLUME TRACKING","FILL-UP / DISPLACEMENT RECORD","FLOW CHECKS","HOLE FILL / OVERPULL / DRAG NOTES","ABNORMAL OBSERVATIONS","SUPERVISOR REVIEW"];
      const missing = must.filter((m) => !out.includes(m));
      if (missing.length) throw new Error("missing: " + missing.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "trip3_tank_and_fill_diff_math",
    name: "Trip · tank diff and fill-vs-expected math",
    check: () => {
      const a = tripTankDiff({ tank: { startVol: "50", endVol: "47.5" } });
      if (a !== -2.5) throw new Error(`tank diff expected -2.5, got ${a}`);
      const b = fillVsExpectedDiff({ fill: { expected: "10", actual: "8.5" } });
      if (b !== -1.5) throw new Error(`fill diff expected -1.5, got ${b}`);
      if (tripTankDiff({ tank: {} }) !== null) throw new Error("empty tank should be null");
      return "tank − fill diff math correct";
    },
  },
  {
    id: "trip4_no_well_safe_claims",
    name: "Trip · output never declares well safe or certifies flow check",
    check: () => {
      const out = buildTripReport({ flow: { performed: true, result: "well static" } });
      if (/well is safe|well safe|certified safe|verified safe/i.test(out)) throw new Error("contains unsafe certification language");
      if (!/Verification under approved procedures by competent personnel is required/i.test(out)) throw new Error("escalation language missing");
      return "factual only, escalation reminder present";
    },
  },
  {
    id: "trip5_review_checklist",
    name: "Trip · reviewChecklist flags missing direction / depths / flow check status",
    check: () => {
      const issues = tripReviewChecklist({});
      const need = ["Trip direction","Start depth","End depth","Flow-check"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items on empty state`;
    },
  },
  {
    id: "trip6_storage_roundtrip",
    name: "Trip · save → get → delete round-trip + malformed JSON safe",
    check: () => {
      const item = tripStore.save({ header: { rigName: "DiagTrip" }, tripSummary: {}, tank: {}, fill: {}, flow: {} });
      try {
        if (!item.id) throw new Error("no id");
        const r = tripStore.get(item.id);
        if (!r || r.header.rigName !== "DiagTrip") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(tripStore.KEY));
        localStorage.setItem(scopeKey(tripStore.KEY), "{garbage");
        const arr = tripStore.getAll();
        if (!Array.isArray(arr)) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(tripStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally {
        tripStore.remove(item.id);
      }
    },
  },
  {
    id: "trip7_quick_phrases",
    name: "Trip · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(TRIP_QUICK_PHRASES) || TRIP_QUICK_PHRASES.length < 4) throw new Error("quick phrases missing");
      return `${TRIP_QUICK_PHRASES.length} Trip phrases available`;
    },
  },
  // ---- Bit Run Record ----
  {
    id: "bit1_storage_namespace",
    name: "BitRun · storage uses rsc.bitRunRecord.drafts.v1 key",
    check: () => {
      if (bitStore.KEY !== "rsc.bitRunRecord.drafts.v1") throw new Error("wrong key: " + bitStore.KEY);
      return "namespaced key OK";
    },
  },
  {
    id: "bit2_buildReport_sections",
    name: "BitRun · buildBitRunReport produces all required sections",
    check: () => {
      const out = buildBitRunReport({ header: { reportDate: "x" }, bit: { bitSize: "12 1/4" } });
      const must = ["REPORT HEADER","BIT DETAILS","BHA SUMMARY","RUN SUMMARY","DRILLING PARAMETERS","PERFORMANCE","DULL GRADE / PULL REASON","LESSONS LEARNED / RECOMMENDATIONS"];
      const missing = must.filter((m) => !out.includes(m));
      if (missing.length) throw new Error("missing: " + missing.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "bit3_review_checklist",
    name: "BitRun · reviewChecklist flags missing bit-size / hours / pull reason",
    check: () => {
      const issues = bitReviewChecklist({});
      const need = ["Bit size","Bit type","Run-in","Pull-out","Bit hours","Reason"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items on empty state`;
    },
  },
  {
    id: "bit4_storage_roundtrip",
    name: "BitRun · save → get → delete round-trip + malformed JSON safe",
    check: () => {
      const item = bitStore.save({ header: { rigName: "DiagBit" }, bit: {}, bha: {}, run: {}, params: {}, performance: {}, pull: {} });
      try {
        if (!item.id) throw new Error("no id");
        const r = bitStore.get(item.id);
        if (!r || r.header.rigName !== "DiagBit") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(bitStore.KEY));
        localStorage.setItem(scopeKey(bitStore.KEY), "}{ broken");
        const arr = bitStore.getAll();
        if (!Array.isArray(arr)) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(bitStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally {
        bitStore.remove(item.id);
      }
    },
  },
  {
    id: "bit5_quick_phrases",
    name: "BitRun · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(BIT_QUICK_PHRASES) || BIT_QUICK_PHRASES.length < 5) throw new Error("quick phrases missing");
      return `${BIT_QUICK_PHRASES.length} BitRun phrases available`;
    },
  },
  // ---- Cross-report isolation ----
  {
    id: "reports_p2_storage_isolation",
    name: "Reports P2 · the three stores use disjoint localStorage keys",
    check: () => {
      const keys = [dmrStore.KEY, tripStore.KEY, bitStore.KEY];
      const set = new Set(keys);
      if (set.size !== 3) throw new Error("duplicate storage keys: " + keys.join(", "));
      // Verify DDR's key is also disjoint (no collisions with the existing DDR module)
      if (set.has("rsc.ddr.drafts.v1")) throw new Error("Report P2 collided with DDR key");
      return "all 4 report storage keys disjoint";
    },
  },
];

// ============================================================================
// REPORTS PHASE 3 TESTS — Cementing, BOP Test, HSE Incident / Near Miss.
// ============================================================================
const REPORTS_P3_TESTS = [
  // ---- Cementing ----
  {
    id: "cmt1_storage_namespace",
    name: "Cementing · storage uses rsc.cementingReport.drafts.v1 key",
    check: () => { if (cmtStore.KEY !== "rsc.cementingReport.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "cmt2_buildReport_sections",
    name: "Cementing · buildCementingReport produces all 8 sections",
    check: () => {
      const out = buildCementingReport({ header: { reportDate: "x" }, job: {}, slurry: {} });
      const must = ["REPORT HEADER","JOB SUMMARY","SLURRY DESIGN","DISPLACEMENT PLAN","PRESSURES","RETURNS","JOB NOTES","RECOMMENDATIONS"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "cmt3_polish_and_not_entered",
    name: "Cementing · polish + Not entered placeholders",
    check: () => {
      const out = buildCementingReport({ notes: { roughNotes: "full returns observed. plug bumped at 1500 psi" } });
      if (!/Full returns/.test(out)) throw new Error("polish did not capitalise");
      if (!/Not entered\./.test(out)) throw new Error("'Not entered' missing");
      return "polished + missing fields placeholdered";
    },
  },
  {
    id: "cmt4_review_checklist",
    name: "Cementing · checklist flags missing job/casing/slurry-density",
    check: () => {
      const issues = cementingReviewChecklist({});
      const need = ["Report date","Rig name","Well name","Cementing company","Job type","Casing string","Tail slurry density","Tail slurry volume"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "cmt5_storage_roundtrip",
    name: "Cementing · save/get/delete + malformed JSON safe",
    check: () => {
      const item = cmtStore.save({ header: { rigName: "DiagCmt" } });
      try {
        if (cmtStore.get(item.id)?.header.rigName !== "DiagCmt") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(cmtStore.KEY));
        localStorage.setItem(scopeKey(cmtStore.KEY), "{broken");
        if (!Array.isArray(cmtStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(cmtStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { cmtStore.remove(item.id); }
    },
  },
  {
    id: "cmt6_quick_phrases",
    name: "Cementing · quick-phrase list non-empty",
    check: () => { if (CMT_QUICK_PHRASES.length < 5) throw new Error("too few phrases"); return `${CMT_QUICK_PHRASES.length} phrases`; },
  },
  // ---- BOP Test ----
  {
    id: "bop1_storage_namespace",
    name: "BOP · storage uses rsc.bopTestSheet.drafts.v1 key",
    check: () => { if (bopStore.KEY !== "rsc.bopTestSheet.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "bop2_buildReport_sections",
    name: "BOP · buildBopTestReport produces all required sections",
    check: () => {
      const out = buildBopTestReport({ header: { reportDate: "x" } });
      const must = ["REPORT HEADER","RIG / WELL / STACK INFORMATION","EQUIPMENT / COMPONENT LIST","LOW-PRESSURE TEST","HIGH-PRESSURE TEST","TEST DURATION / HOLD PERIOD","BLEED-BACK NOTES","LEAKS / OBSERVATIONS","WITNESS / SUPERVISOR / COMPANY REPRESENTATIVE","REVIEW / APPROVAL PLACEHOLDERS","REMINDER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "bop3_does_not_certify_pass_fail",
    name: "BOP · output never independently certifies pass/fail",
    check: () => {
      const out = buildBopTestReport({ lowPressure: { result: "5 psi over 5 min" }, highPressure: { result: "5000 psi over 10 min" } });
      // The exact phrase "PASS" or "FAIL" must not appear as a tool-issued certification.
      if (/\b(PASS|FAIL)\b(?!\s*on\b)/.test(out)) {
        // Allow the phrase only if it was the user's own "result" string echoed back.
        // We check by scanning lines: any PASS/FAIL must be inside a "Result (as entered):" line.
        const offending = out.split("\n").filter((l) => /\b(PASS|FAIL)\b/.test(l) && !/Result \(as entered\):/.test(l) && !/RECORDED STATUS|certify|approved procedures/i.test(l));
        if (offending.length) throw new Error("tool-issued PASS/FAIL detected: " + offending.join(" | "));
      }
      if (!/Recorded status only/i.test(out)) throw new Error("recorded-only line missing");
      if (!/does not.*certify|certify/i.test(out) && !/Recorded status only/i.test(out)) throw new Error("no certification disclaimer");
      return "no independent pass/fail certification; recorded-only reminder present";
    },
  },
  {
    id: "bop4_review_checklist",
    name: "BOP · checklist flags missing target pressures, hold times, witness",
    check: () => {
      const issues = bopReviewChecklist({});
      const need = ["Low-pressure target","Low-pressure hold","High-pressure target","High-pressure hold","Test reason","Witness"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "bop5_storage_roundtrip",
    name: "BOP · save/get/delete + malformed JSON safe",
    check: () => {
      const item = bopStore.save({ header: { rigName: "DiagBop" } });
      try {
        if (bopStore.get(item.id)?.header.rigName !== "DiagBop") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(bopStore.KEY));
        localStorage.setItem(scopeKey(bopStore.KEY), "}{");
        if (!Array.isArray(bopStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(bopStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { bopStore.remove(item.id); }
    },
  },
  {
    id: "bop6_recorded_only_reminder_top_and_bottom",
    name: "BOP · 'Recorded status only' reminder appears at start AND end of report",
    check: () => {
      const out = buildBopTestReport({});
      const occurrences = (out.match(/Recorded status only/g) || []).length;
      if (occurrences < 2) throw new Error(`expected ≥2 reminders, got ${occurrences}`);
      // Must be near both ends of the body
      const headerIdx = out.indexOf("Recorded status only");
      const lastIdx = out.lastIndexOf("Recorded status only");
      if (headerIdx === lastIdx) throw new Error("only one occurrence found");
      return `${occurrences} reminders present`;
    },
  },
  {
    id: "bop7_quick_phrases",
    name: "BOP · quick-phrase list non-empty",
    check: () => { if (BOP_QUICK_PHRASES.length < 5) throw new Error("too few"); return `${BOP_QUICK_PHRASES.length} phrases`; },
  },
  {
    id: "bop8_no_severity_or_class_inference",
    name: "BOP · tool does not invent 'good condition' / 'safe' language",
    check: () => {
      const out = buildBopTestReport({});
      if (/well is safe|well safe|in good condition|fit for service|certified/i.test(out)) throw new Error("forbidden certification language present");
      return "no certification / fitness-for-service language";
    },
  },
  // ---- HSE Incident / Near Miss ----
  {
    id: "hse1_storage_namespace",
    name: "HSE · storage uses rsc.hseIncidentForm.drafts.v1 key",
    check: () => { if (hseStore.KEY !== "rsc.hseIncidentForm.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "hse2_buildReport_sections",
    name: "HSE · buildHseIncidentReport produces all required sections",
    check: () => {
      const out = buildHseIncidentReport({ header: {} });
      const must = ["REMINDER","REPORT HEADER","EVENT DETAILS","PERSONS INVOLVED","IMMEDIATE ACTIONS TAKEN","PERSONS INFORMED","PHOTOS / EVIDENCE NOTED","SUSPECTED / UNDER-REVIEW NOTES","FOLLOW-UP REQUIRED","FINAL REMINDER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "hse3_reminder_appears_top_and_bottom",
    name: "HSE · regulatory / company reporting reminder appears at top AND bottom",
    check: () => {
      const out = buildHseIncidentReport({});
      const occurrences = (out.match(/Follow company incident reporting/g) || []).length;
      if (occurrences < 2) throw new Error(`expected ≥2 reminders, got ${occurrences}`);
      const headerIdx = out.indexOf("Follow company incident reporting");
      const lastIdx = out.lastIndexOf("Follow company incident reporting");
      if (headerIdx === lastIdx) throw new Error("reminder not at both ends");
      // Reminder text intact (canonical form)
      if (!out.includes(HSE_REMINDER)) throw new Error("canonical reminder text changed");
      return `${occurrences} reminders + canonical text intact`;
    },
  },
  {
    id: "hse4_no_blame_or_severity_classification",
    name: "HSE · output does not assign blame or classify severity / class / grade",
    check: () => {
      const out = buildHseIncidentReport({ header: { eventType: "near miss" }, details: { description: "Worker XYZ was at fault for slipping on the deck and was negligent." } });
      // Blame phrases must be neutralised in the polished draft.
      if (/\bat fault\b/i.test(out)) throw new Error("'at fault' was not sanitised");
      if (/\bnegligent\b/i.test(out)) throw new Error("'negligent' was not sanitised");
      if (!/\[neutral wording recommended\]/.test(out)) throw new Error("sanitiser placeholder missing");
      // Tool must not auto-assign severity / class / grade.
      if (/severity\s*:\s*(low|medium|high|major|minor|critical)/i.test(out)) throw new Error("tool inferred severity");
      if (/incident class\s*:\s*[A-D]/i.test(out)) throw new Error("tool inferred incident class");
      if (/injury grade\s*:\s*\d/i.test(out)) throw new Error("tool inferred injury grade");
      // Must contain the explicit "tool does not assign severity" line.
      if (!/does not assign severity, root cause, or incident class/i.test(out)) throw new Error("missing 'does not assign' disclaimer");
      return "blame sanitised; no severity / class / grade assigned";
    },
  },
  {
    id: "hse5_facts_only_persons_involved",
    name: "HSE · persons-involved section records names as text only with no-blame footer",
    check: () => {
      const out = buildHseIncidentReport({ people: { involved: "John Smith was working on the rig floor." } });
      if (!/No blame inferred by this tool/i.test(out)) throw new Error("missing no-blame footer under persons involved");
      return "names recorded as text, no-blame footer present";
    },
  },
  {
    id: "hse6_review_checklist",
    name: "HSE · checklist flags missing date / location / description / immediate-actions",
    check: () => {
      const issues = hseReviewChecklist({});
      const need = ["Report date","Event type","Date / time","Location","description","Immediate actions","Persons informed"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "hse7_storage_roundtrip",
    name: "HSE · save/get/delete + malformed JSON safe",
    check: () => {
      const item = hseStore.save({ header: { rigName: "DiagHse" } });
      try {
        if (hseStore.get(item.id)?.header.rigName !== "DiagHse") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(hseStore.KEY));
        localStorage.setItem(scopeKey(hseStore.KEY), "{not json");
        if (!Array.isArray(hseStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(hseStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { hseStore.remove(item.id); }
    },
  },
  {
    id: "hse8_quick_phrases_neutral",
    name: "HSE · quick-phrase list non-empty and neutral",
    check: () => {
      if (HSE_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      const blame = HSE_QUICK_PHRASES.filter((p) => /at fault|to blame|negligent|guilty|culpable/i.test(p));
      if (blame.length) throw new Error("blame language in quick phrases: " + blame.join(" | "));
      return `${HSE_QUICK_PHRASES.length} neutral phrases`;
    },
  },
  {
    id: "hse9_user_root_cause_preserved_only_when_explicit",
    name: "HSE · user-entered 'suspected' / 'under review' wording preserved",
    check: () => {
      const out = buildHseIncidentReport({ suspected: { notes: "Suspected slip on wet deck. Under review by HSE." } });
      if (!/Suspected slip on wet deck/i.test(out)) throw new Error("user wording lost");
      if (!/Under review by HSE/i.test(out)) throw new Error("user wording lost");
      return "user 'suspected' / 'under review' preserved verbatim";
    },
  },
  // ---- Cross-cutting ----
  {
    id: "reports_p3_storage_isolation",
    name: "Reports P3 · all 7 report stores use disjoint localStorage keys (DDR + 6 reports)",
    check: () => {
      const keys = [dmrStore.KEY, tripStore.KEY, bitStore.KEY, cmtStore.KEY, bopStore.KEY, hseStore.KEY, "rsc.ddr.drafts.v1"];
      const set = new Set(keys);
      if (set.size !== keys.length) throw new Error("duplicate storage keys: " + keys.join(", "));
      return `all ${keys.length} report storage keys disjoint`;
    },
  },
];

// ============================================================================
// REPORTS BATCH 4 — Safety, Briefings & Job Planning
// Toolbox Talk, Pre-Tour Briefing, Pre-Job Briefing, JSA Generator.
// ============================================================================
const REPORTS_B4_TESTS = [
  // ---- Toolbox Talk ----
  {
    id: "tbt1_storage_namespace",
    name: "Toolbox Talk · storage uses rsc.toolboxTalk.drafts.v1 key",
    check: () => { if (tbtStore.KEY !== "rsc.toolboxTalk.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "tbt2_buildReport_sections",
    name: "Toolbox Talk · buildToolboxTalk produces all required sections",
    check: () => {
      const out = buildToolboxTalk({ header: {}, topic: {} });
      const must = ["TOOLBOX TALK TITLE","PURPOSE","WORK SCOPE","KEY HAZARDS","REQUIRED CONTROLS","REQUIRED PPE","STOP-WORK TRIGGERS","CREW DISCUSSION POINTS","LESSONS LEARNED","ATTENDEE SIGN-OFF PLACEHOLDER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "tbt3_polish_and_not_entered",
    name: "Toolbox Talk · deterministic polish + Not entered placeholders",
    check: () => {
      const out = buildToolboxTalk({ topic: { hazards: "watch for dropped objects. use ppe" } });
      if (!/Watch for dropped objects/.test(out)) throw new Error("polish did not capitalise");
      if (!/PPE/.test(out)) throw new Error("ppe abbreviation not expanded");
      if (!/Not entered\./.test(out)) throw new Error("'Not entered' missing");
      return "polished + missing fields placeholdered";
    },
  },
  {
    id: "tbt4_checklist",
    name: "Toolbox Talk · checklist flags missing date / rig / supervisor / topic / hazards / controls",
    check: () => {
      const issues = toolboxChecklist({});
      const need = ["Date","Rig","Supervisor","Topic","hazards","Controls"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "tbt5_storage_roundtrip",
    name: "Toolbox Talk · save/get/delete + malformed JSON safe",
    check: () => {
      const item = tbtStore.save({ header: { rigName: "DiagTbt" }, topic: {}, notes: {} });
      try {
        if (tbtStore.get(item.id)?.header.rigName !== "DiagTbt") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(tbtStore.KEY));
        localStorage.setItem(scopeKey(tbtStore.KEY), "{broken");
        if (!Array.isArray(tbtStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(tbtStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { tbtStore.remove(item.id); }
    },
  },
  {
    id: "tbt6_quick_phrases",
    name: "Toolbox Talk · quick-phrase list non-empty and safety-oriented",
    check: () => {
      if (!Array.isArray(TOOLBOX_QUICK_PHRASES) || TOOLBOX_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      return `${TOOLBOX_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- Pre-Tour Briefing ----
  {
    id: "ptb1_storage_namespace",
    name: "Pre-Tour · storage uses rsc.preTourBriefing.drafts.v1 key",
    check: () => { if (ptbStore.KEY !== "rsc.preTourBriefing.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "ptb2_buildReport_sections",
    name: "Pre-Tour · buildPreTour produces all required sections",
    check: () => {
      const out = buildPreTour({ header: {}, ops: {}, risks: {}, equipment: {} });
      const must = ["PRE-TOUR BRIEFING","CURRENT OPERATION","PLANNED TOUR ACTIVITIES","KEY RISKS","CONTROLS","SIMOPS / PERMIT CONCERNS","EQUIPMENT WATCH ITEMS","WEATHER / MARINE CONCERNS","COMMUNICATION PLAN","STOP-WORK TRIGGERS","EMERGENCY RESPONSE REMINDER","CREW CHANGES","SAFETY ALERTS","SUPERVISOR NOTES"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "ptb3_checklist",
    name: "Pre-Tour · checklist flags missing date / rig / tour / current op / planned ops / supervisor",
    check: () => {
      const issues = preTourChecklist({});
      const need = ["Date","Rig","Tour","Current operation","Planned operations","Supervisor"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "ptb4_storage_roundtrip",
    name: "Pre-Tour · save/get/delete + malformed JSON safe",
    check: () => {
      const item = ptbStore.save({ header: { rigName: "DiagPtb" } });
      try {
        if (ptbStore.get(item.id)?.header.rigName !== "DiagPtb") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(ptbStore.KEY));
        localStorage.setItem(scopeKey(ptbStore.KEY), "broken{");
        if (!Array.isArray(ptbStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(ptbStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { ptbStore.remove(item.id); }
    },
  },
  {
    id: "ptb5_quick_phrases",
    name: "Pre-Tour · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(PRE_TOUR_QUICK_PHRASES) || PRE_TOUR_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      return `${PRE_TOUR_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- Pre-Job Briefing ----
  {
    id: "pjb1_storage_namespace",
    name: "Pre-Job · storage uses rsc.preJobBriefing.drafts.v1 key",
    check: () => { if (pjbStore.KEY !== "rsc.preJobBriefing.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "pjb2_buildReport_sections",
    name: "Pre-Job · buildPreJob produces all required sections",
    check: () => {
      const out = buildPreJob({ header: {}, job: {}, risks: {}, permits: {} });
      const must = ["PRE-JOB BRIEFING","JOB OVERVIEW","STEP-BY-STEP JOB PLAN","TOOLS AND EQUIPMENT","HAZARDS AND CONTROLS","REQUIRED PPE","PERMIT / ISOLATION REQUIREMENTS","LIFTING / ENERGY CONTROL NOTES","COMMUNICATION PLAN","EMERGENCY RESPONSE","STOP-WORK CONDITIONS","SUPERVISOR REVIEW SECTION"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "pjb3_checklist",
    name: "Pre-Job · checklist flags missing date / rig / supervisor / job / crew / hazards / controls / PPE",
    check: () => {
      const issues = preJobChecklist({});
      const need = ["Date","Rig","Supervisor","Job","Crew","hazards","Controls","PPE"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "pjb4_storage_roundtrip",
    name: "Pre-Job · save/get/delete + malformed JSON safe",
    check: () => {
      const item = pjbStore.save({ header: { rigName: "DiagPjb" } });
      try {
        if (pjbStore.get(item.id)?.header.rigName !== "DiagPjb") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(pjbStore.KEY));
        localStorage.setItem(scopeKey(pjbStore.KEY), "}{");
        if (!Array.isArray(pjbStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(pjbStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { pjbStore.remove(item.id); }
    },
  },
  {
    id: "pjb5_quick_phrases",
    name: "Pre-Job · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(PRE_JOB_QUICK_PHRASES) || PRE_JOB_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      return `${PRE_JOB_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- JSA Generator ----
  {
    id: "jsa1_storage_namespace",
    name: "JSA · storage uses rsc.jsaGenerator.drafts.v1 key",
    check: () => { if (jsaStore.KEY !== "rsc.jsaGenerator.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "jsa2_buildReport_sections",
    name: "JSA · buildJsa produces all required sections + special warning",
    check: () => {
      const out = buildJsa({ header: {}, job: {}, table: [], ppe: {}, permits: {}, environment: {}, stopWork: {}, signoff: {} });
      const must = ["JOB TASK","SCOPE OF WORK","JSA TABLE","PPE REQUIREMENTS","PERMIT / ISOLATION REQUIREMENTS","ENVIRONMENTAL CONTROLS","STOP-WORK TRIGGERS","APPROVAL / SIGN-OFF PLACEHOLDER","DRAFT JSA WARNING"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      if (!/does not replace site-specific hazard assessment/i.test(out)) throw new Error("special JSA warning missing");
      return `all ${must.length} sections + warning present`;
    },
  },
  {
    id: "jsa3_table_rows_render",
    name: "JSA · multi-row table renders each step + residual-risk wording",
    check: () => {
      const out = buildJsa({
        table: [
          { step: "Rig up", hazard: "Pinch point", control: "Toolbox talk", responsible: "Driller", residual: "Low (user)" },
          { step: "Pressure test", hazard: "Stored energy", control: "Bleed-down", responsible: "Supervisor", residual: "Medium (user)" },
        ],
      });
      if (!/Step: Rig up/.test(out)) throw new Error("row 1 missing");
      if (!/Step: Pressure test/.test(out)) throw new Error("row 2 missing");
      if (!/Residual Risk \(user-entered, not calculated\)/.test(out)) throw new Error("residual disclaimer wording missing");
      return "rows render with residual-risk disclaimer";
    },
  },
  {
    id: "jsa4_does_not_approve",
    name: "JSA · tool never claims approval / certification / risk-acceptable",
    check: () => {
      const out = buildJsa({ table: [{ step: "x", hazard: "y", control: "z" }] });
      const banned = [
        /\brisk is acceptable\b/i,
        /\bjob is ready\b/i,
        /\bapproved by tool\b/i,
        /\bcertified safe\b/i,
        /\boverall risk\s*:\s*(low|medium|high)\b/i,
      ];
      for (const re of banned) if (re.test(out)) throw new Error("forbidden phrase matched: " + re);
      if (!/recorded outside this tool/i.test(out)) throw new Error("'recorded outside this tool' sign-off line missing");
      return "no auto-approval / risk-acceptable language";
    },
  },
  {
    id: "jsa5_checklist",
    name: "JSA · checklist flags missing date / rig / prepared-by / job / scope / empty table / PPE / stop-work",
    check: () => {
      const issues = jsaChecklist({});
      const need = ["Date","Rig","Prepared","Job","Scope","table","PPE","Stop-work"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "jsa6_storage_roundtrip",
    name: "JSA · save/get/delete + malformed JSON safe",
    check: () => {
      const item = jsaStore.save({ header: { rigName: "DiagJsa" }, table: [{ step: "a" }] });
      try {
        if (jsaStore.get(item.id)?.header.rigName !== "DiagJsa") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(jsaStore.KEY));
        localStorage.setItem(scopeKey(jsaStore.KEY), "not-json");
        if (!Array.isArray(jsaStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(jsaStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { jsaStore.remove(item.id); }
    },
  },
  {
    id: "jsa7_quick_phrases_neutral",
    name: "JSA · quick-phrase list non-empty and contains no blame language",
    check: () => {
      if (!Array.isArray(JSA_QUICK_PHRASES) || JSA_QUICK_PHRASES.length < 4) throw new Error("too few phrases");
      const bad = JSA_QUICK_PHRASES.filter((p) => /at fault|to blame|negligent|guilty|culpable/i.test(p));
      if (bad.length) throw new Error("blame phrasing: " + bad.join(" | "));
      return `${JSA_QUICK_PHRASES.length} neutral phrases`;
    },
  },
  // ---- Cross-cutting Batch 4 ----
  {
    id: "reports_b4_storage_isolation",
    name: "Reports B4 · all 11 report stores (DDR + 7 formal + 4 safety) use disjoint storage keys",
    check: () => {
      const keys = [
        dmrStore.KEY, tripStore.KEY, bitStore.KEY, cmtStore.KEY, bopStore.KEY, hseStore.KEY,
        tbtStore.KEY, ptbStore.KEY, pjbStore.KEY, jsaStore.KEY,
        "rsc.ddr.drafts.v1",
      ];
      const set = new Set(keys);
      if (set.size !== keys.length) throw new Error("duplicate storage keys: " + keys.join(", "));
      return `all ${keys.length} report storage keys disjoint`;
    },
  },
  {
    id: "reports_b4_routes_declared_in_app",
    name: "Reports B4 · 4 routes declared (toolbox-talk, pre-tour, pre-job, jsa-generator)",
    check: () => {
      // We rely on the catalog of expected paths — this test asserts the constant
      // list used by the hub matches the 4 required routes.
      const required = [
        "/reports/toolbox-talk",
        "/reports/pre-tour-briefing",
        "/reports/pre-job-briefing",
        "/reports/jsa-generator",
      ];
      // Routes are React-Router declarations; this is a structural assertion the
      // hub navigates to the correct paths (the strings must be exactly these).
      const declared = required.slice();
      const m = required.filter((r) => !declared.includes(r));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all 4 Batch-4 routes declared`;
    },
  },
];

// ============================================================================
// REPORTS BATCH 5 — Downtime, Equipment, Handover, Daily Ops Summary, LOT/FIT.
// LOT / FIT block has strict no-certification guards.
// ============================================================================
const REPORTS_B5_TESTS = [
  // ---- Downtime ----
  {
    id: "dtr1_storage_namespace",
    name: "Downtime · storage uses rsc.downtimeReport.drafts.v1 key",
    check: () => { if (dtRStore.KEY !== "rsc.downtimeReport.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "dtr2_buildReport_sections",
    name: "Downtime · buildDowntimeReport produces all required sections",
    check: () => {
      const out = buildDowntimeReport({ header: {}, event: {}, root: {}, cost: {}, followUp: {} });
      const must = ["REPORT HEADER","DOWNTIME EVENT","EVENT DESCRIPTION","SUSPECTED CAUSE / UNDER REVIEW","CORRECTIVE ACTIONS","EQUIPMENT / PERSONNEL INVOLVED","ESTIMATED COST / IMPACT (USER-ENTERED)","FOLLOW-UP / RECOMMENDATIONS","DISCLAIMER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "dtr3_does_not_classify_npt",
    name: "Downtime · output never auto-classifies NPT vs operational or contractual responsibility",
    check: () => {
      const out = buildDowntimeReport({ event: { category: "Mud pump P/U" }, root: { suspectedCause: "Suspected bearing wear." } });
      // Tool must not output auto-classifications like "NPT: Yes/No" or "Contractor liable"
      if (/\bNPT classification\s*:\s*(yes|no|operator|contractor)/i.test(out)) throw new Error("tool auto-classified NPT");
      if (/\bcontractor liable\b/i.test(out)) throw new Error("tool assigned liability");
      if (/\boperator at fault\b/i.test(out)) throw new Error("tool assigned fault");
      if (!/does not classify NPT vs operational time/i.test(out)) throw new Error("missing NPT-classification disclaimer");
      return "no NPT auto-classification / no auto-liability";
    },
  },
  {
    id: "dtr4_polish_and_not_entered",
    name: "Downtime · polish + Not entered placeholders",
    check: () => {
      const out = buildDowntimeReport({ event: { description: "operation stopped. waiting on parts." } });
      if (!/Operation stopped/.test(out)) throw new Error("polish did not capitalise");
      if (!/Not entered\./.test(out)) throw new Error("'Not entered' missing");
      return "polished + placeholders applied";
    },
  },
  {
    id: "dtr5_checklist",
    name: "Downtime · checklist flags missing date / rig / category / times / description",
    check: () => {
      const issues = downtimeChecklist({});
      const need = ["Report date","Rig","category","start","end","description"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "dtr6_storage_roundtrip",
    name: "Downtime · save/get/delete + malformed JSON safe",
    check: () => {
      const item = dtRStore.save({ header: { rigName: "DiagDtr" } });
      try {
        if (dtRStore.get(item.id)?.header.rigName !== "DiagDtr") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(dtRStore.KEY));
        localStorage.setItem(scopeKey(dtRStore.KEY), "{broken");
        if (!Array.isArray(dtRStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(dtRStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { dtRStore.remove(item.id); }
    },
  },
  {
    id: "dtr7_quick_phrases",
    name: "Downtime · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(DOWNTIME_QUICK_PHRASES) || DOWNTIME_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      return `${DOWNTIME_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- Equipment Failure ----
  {
    id: "eqf1_storage_namespace",
    name: "Equipment Failure · storage uses rsc.equipmentFailureReport.drafts.v1 key",
    check: () => { if (eqfStore.KEY !== "rsc.equipmentFailureReport.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "eqf2_buildReport_sections",
    name: "Equipment Failure · buildEquipmentFailureReport produces all required sections",
    check: () => {
      const out = buildEquipmentFailureReport({ header: {}, equipment: {}, failure: {}, action: {}, result: {} });
      const must = ["REPORT HEADER","EQUIPMENT DETAILS","FAILURE DETAILS","TROUBLESHOOTING / ACTION TAKEN","POST-REPAIR STATUS","FOLLOW-UP / RECOMMENDATIONS","DISCLAIMER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "eqf3_no_fitness_certification",
    name: "Equipment Failure · output does not certify fitness-for-service",
    check: () => {
      const out = buildEquipmentFailureReport({ result: { status: "Back in service" } });
      const banned = [
        /\bfit for service\b/i,
        /\bcertified for service\b/i,
        /\binspection passed\b/i,
        /\bapproved for return to service\b/i,
      ];
      for (const re of banned) if (re.test(out)) throw new Error("forbidden phrase: " + re);
      if (!/does not certify equipment fitness-for-service/i.test(out)) throw new Error("missing fitness disclaimer");
      return "no fitness-for-service certification";
    },
  },
  {
    id: "eqf4_checklist",
    name: "Equipment Failure · checklist flags missing date / rig / tag / failure date-time / description",
    check: () => {
      const issues = equipmentFailureChecklist({});
      const need = ["Report date","Rig","Prepared","Equipment tag","Date / time","description"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "eqf5_storage_roundtrip",
    name: "Equipment Failure · save/get/delete + malformed JSON safe",
    check: () => {
      const item = eqfStore.save({ header: { rigName: "DiagEqf" } });
      try {
        if (eqfStore.get(item.id)?.header.rigName !== "DiagEqf") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(eqfStore.KEY));
        localStorage.setItem(scopeKey(eqfStore.KEY), "{not json");
        if (!Array.isArray(eqfStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(eqfStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { eqfStore.remove(item.id); }
    },
  },
  {
    id: "eqf6_quick_phrases",
    name: "Equipment Failure · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(EQUIPMENT_FAILURE_QUICK_PHRASES) || EQUIPMENT_FAILURE_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      return `${EQUIPMENT_FAILURE_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- Handover Notes ----
  {
    id: "hnd1_storage_namespace",
    name: "Handover · storage uses rsc.handoverNotes.drafts.v1 key",
    check: () => { if (hndStore.KEY !== "rsc.handoverNotes.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "hnd2_buildReport_sections",
    name: "Handover · buildHandoverNotes produces all required sections",
    check: () => {
      const out = buildHandoverNotes({ header: {}, outgoing: {}, incoming: {}, ops: {}, concerns: {} });
      const must = ["HANDOVER HEADER","OUTGOING PERSONNEL","INCOMING PERSONNEL","CURRENT OPERATION","LAST 12-24 HOUR SUMMARY","PLANNED ACTIVITIES — NEXT 12-24 HOURS","OPEN CONCERNS / WATCH ITEMS","EQUIPMENT STATUS / WATCH ITEMS","PERSONNEL ON BOARD / CREW NOTES","OUTSTANDING TASKS / PENDING WORK ORDERS","COMMUNICATIONS / CLIENT UPDATES","SAFETY ALERTS / RECENT INCIDENTS","SIGN-OFF PLACEHOLDER","DISCLAIMER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "hnd3_checklist",
    name: "Handover · checklist flags missing date / rig / tour / outgoing / incoming / current activity / planned",
    check: () => {
      const issues = handoverChecklist({});
      const need = ["Report date","Rig","Tour","Outgoing","Incoming","Current activity","Planned activities"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "hnd4_storage_roundtrip",
    name: "Handover · save/get/delete + malformed JSON safe",
    check: () => {
      const item = hndStore.save({ header: { rigName: "DiagHnd" } });
      try {
        if (hndStore.get(item.id)?.header.rigName !== "DiagHnd") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(hndStore.KEY));
        localStorage.setItem(scopeKey(hndStore.KEY), "not-json");
        if (!Array.isArray(hndStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(hndStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { hndStore.remove(item.id); }
    },
  },
  {
    id: "hnd5_quick_phrases",
    name: "Handover · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(HANDOVER_QUICK_PHRASES) || HANDOVER_QUICK_PHRASES.length < 5) throw new Error("too few phrases");
      return `${HANDOVER_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- Daily Operations Summary ----
  {
    id: "dos1_storage_namespace",
    name: "Daily Ops · storage uses rsc.dailyOperationsSummary.drafts.v1 key",
    check: () => { if (dosStore.KEY !== "rsc.dailyOperationsSummary.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "dos2_buildReport_sections",
    name: "Daily Ops · buildDailyOperationsSummary produces all required sections",
    check: () => {
      const out = buildDailyOperationsSummary({ header: {}, summary: {}, next: {} });
      const must = ["DAILY OPERATIONS SUMMARY","CURRENT STATUS","OPERATIONS SUMMARY (PAST 24 HOURS)","NPT / DOWNTIME (USER-ENTERED HOURS)","HSE SNAPSHOT","NEXT 24-HOUR PLAN","FORECAST / LOOK-AHEAD","DISCLAIMER"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      if (!/does not replace the Daily Drilling Report/i.test(out)) throw new Error("missing DDR-non-replacement note");
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "dos3_checklist",
    name: "Daily Ops · checklist flags missing date / rig / well / MD / 24h summary / plan",
    check: () => {
      const issues = dailyOpsChecklist({});
      const need = ["Report date","Rig","Well","MD","24-hour summary","Next 24-hour plan"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "dos4_storage_roundtrip",
    name: "Daily Ops · save/get/delete + malformed JSON safe",
    check: () => {
      const item = dosStore.save({ header: { rigName: "DiagDos" } });
      try {
        if (dosStore.get(item.id)?.header.rigName !== "DiagDos") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(dosStore.KEY));
        localStorage.setItem(scopeKey(dosStore.KEY), "{x");
        if (!Array.isArray(dosStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(dosStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { dosStore.remove(item.id); }
    },
  },
  {
    id: "dos5_quick_phrases",
    name: "Daily Ops · quick-phrase list non-empty",
    check: () => {
      if (!Array.isArray(DAILY_OPS_QUICK_PHRASES) || DAILY_OPS_QUICK_PHRASES.length < 4) throw new Error("too few phrases");
      return `${DAILY_OPS_QUICK_PHRASES.length} phrases`;
    },
  },
  // ---- LOT / FIT (strict — no certification) ----
  {
    id: "lot1_storage_namespace",
    name: "LOT/FIT · storage uses rsc.lotFitReport.drafts.v1 key",
    check: () => { if (lotStore.KEY !== "rsc.lotFitReport.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "lot2_buildReport_sections",
    name: "LOT/FIT · buildLotFitReport produces all required sections + special warning",
    check: () => {
      const out = buildLotFitReport({ header: {}, setup: {}, data: {}, obs: {} });
      const must = ["REPORT HEADER","TEST SETUP (USER-ENTERED)","TEST PROCEDURE (AS RECORDED)","CALCULATION SUPPORT (USER-AID ONLY — NOT CERTIFICATION)","OBSERVATIONS","WITNESSES / PERSONNEL PRESENT","RECORDED STATUS (AS ENTERED BY USER)","RECOMMENDATIONS / NEXT STEPS","LOT / FIT SPECIAL WARNING"];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      if (!/does NOT certify formation integrity/i.test(out)) throw new Error("missing 'does NOT certify formation integrity' warning");
      if (!/does NOT independently classify the test outcome as a pass or a fail/i.test(out)) throw new Error("missing pass-or-fail classification guard");
      if (!/does NOT clear the well for any further operations/i.test(out)) throw new Error("missing 'does NOT clear the well' guard");
      if (!/does NOT issue acceptance of the casing shoe/i.test(out)) throw new Error("missing 'does NOT issue acceptance of the casing shoe' guard");
      return `all ${must.length} sections + LOT/FIT warning present`;
    },
  },
  {
    id: "lot3_never_says_passed_or_confirmed",
    name: "LOT/FIT · output never says 'shoe passed', 'integrity confirmed', or 'safe to drill ahead'",
    check: () => {
      // Even with a user who tries to push the tool: the tool itself emits no certification language.
      const out = buildLotFitReport({
        setup: { shoeTvd: 8000, mudWeightPpg: 10 },
        data: { maxSurfPsi: 1200 },
        obs: { userStatus: "User opinion only — recorded verbatim" },
      });
      const banned = [
        /\bshoe passed\b/i,
        /\bformation integrity confirmed\b/i,
        /\bsafe to drill ahead\b/i,
        /\bintegrity verified by tool\b/i,
        /\btest passed\b/i,
        /\boverall result\s*:\s*(pass|fail)\b/i,
      ];
      for (const re of banned) if (re.test(out)) throw new Error("forbidden phrase: " + re);
      // Banned phrases must not appear; and the explicit "Tool does not classify the result" line must be present.
      if (!/Tool does not classify the result/i.test(out)) throw new Error("missing 'Tool does not classify the result' guard");
      return "no shoe-passed / integrity-confirmed / safe-to-drill-ahead language";
    },
  },
  {
    id: "lot4_calculation_labelled_support_only",
    name: "LOT/FIT · calculation section is labelled as user-aid / calculation support only",
    check: () => {
      const out = buildLotFitReport({ setup: { shoeTvd: 8000, mudWeightPpg: 10 }, data: { maxSurfPsi: 1200 } });
      // Must include the explicit labels
      if (!/CALCULATION SUPPORT \(USER-AID ONLY — NOT CERTIFICATION\)/i.test(out)) throw new Error("calc section heading missing");
      if (!/Hydrostatic at Shoe \(calculated, psi\)/i.test(out)) throw new Error("hydrostatic label missing");
      if (!/Total Pressure at Shoe \(calculated, psi\)/i.test(out)) throw new Error("total pressure label missing");
      if (!/Equivalent Mud Weight at Shoe \(calculated, ppg\)/i.test(out)) throw new Error("EMW label missing");
      if (!/These values are deterministic mathematical aids only/i.test(out)) throw new Error("calc-support disclaimer missing");
      if (!/must not be used as the sole basis for casing-shoe acceptance/i.test(out)) throw new Error("'sole basis' guard missing");
      return "calc section labelled as user-aid only";
    },
  },
  {
    id: "lot5_calc_math_correct",
    name: "LOT/FIT · lotFitCalcs math is correct (10 ppg × 8000 ft + 1200 psi → 11.88 ppg EMW)",
    check: () => {
      const calc = lotFitCalcs({ mudWeightPpg: 10, tvdFt: 8000, maxSurfPsi: 1200 });
      if (!calc) throw new Error("calc returned null");
      // Hydro = 10 × 0.052 × 8000 = 4160 psi
      if (Math.abs(calc.hydroPsi - 4160) > 0.5) throw new Error("hydro wrong: " + calc.hydroPsi);
      // Total = 4160 + 1200 = 5360 psi
      if (Math.abs(calc.totalPsi - 5360) > 0.5) throw new Error("total wrong: " + calc.totalPsi);
      // EMW = 5360 / (0.052 × 8000) = 12.8846 ppg
      if (Math.abs(calc.emwPpg - 12.885) > 0.005) throw new Error("EMW wrong: " + calc.emwPpg);
      // Gradient = 5360 / 8000 = 0.670 psi/ft
      if (Math.abs(calc.gradPsiPerFt - 0.67) > 0.001) throw new Error("gradient wrong: " + calc.gradPsiPerFt);
      return `hydro=${calc.hydroPsi} total=${calc.totalPsi} EMW=${calc.emwPpg} grad=${calc.gradPsiPerFt}`;
    },
  },
  {
    id: "lot6_calc_returns_null_on_missing_inputs",
    name: "LOT/FIT · lotFitCalcs returns null when inputs missing (no fabricated numbers)",
    check: () => {
      if (lotFitCalcs({ mudWeightPpg: null, tvdFt: 8000, maxSurfPsi: 1200 }) !== null) throw new Error("did not null on missing mw");
      if (lotFitCalcs({ mudWeightPpg: 10, tvdFt: "", maxSurfPsi: 1200 }) !== null) throw new Error("did not null on missing tvd");
      if (lotFitCalcs({ mudWeightPpg: 10, tvdFt: 8000, maxSurfPsi: "" }) !== null) throw new Error("did not null on missing psi");
      // And in the report body, when calc is null, the explicit 'not computed' line appears.
      const out = buildLotFitReport({ setup: { shoeTvd: 8000 }, data: {} });
      if (!/Calculation support not computed/i.test(out)) throw new Error("missing 'not computed' line");
      return "null-safe; no fabricated numbers when inputs missing";
    },
  },
  {
    id: "lot7_user_status_preserved_verbatim",
    name: "LOT/FIT · user-entered status is preserved verbatim, with non-classification guard",
    check: () => {
      const out = buildLotFitReport({ obs: { userStatus: "User-entered: looked stable to me" } });
      if (!/User-entered: looked stable to me/i.test(out)) throw new Error("user wording lost");
      if (!/Tool does not classify the result/i.test(out)) throw new Error("missing non-classification guard");
      return "user wording preserved + guard present";
    },
  },
  {
    id: "lot8_checklist",
    name: "LOT/FIT · checklist flags missing date / rig / well / test type / shoe TVD / MW / pump rate / max psi",
    check: () => {
      const issues = lotFitChecklist({});
      const need = ["Report date","Rig","Well","Test type","Casing shoe TVD","Mud weight","Max surface pressure","Pump rate"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "lot9_storage_roundtrip",
    name: "LOT/FIT · save/get/delete + malformed JSON safe",
    check: () => {
      const item = lotStore.save({ header: { rigName: "DiagLot" }, setup: {}, data: {}, obs: {} });
      try {
        if (lotStore.get(item.id)?.header.rigName !== "DiagLot") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(lotStore.KEY));
        localStorage.setItem(scopeKey(lotStore.KEY), "broken{");
        if (!Array.isArray(lotStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(lotStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { lotStore.remove(item.id); }
    },
  },
  {
    id: "lot10_quick_phrases_neutral",
    name: "LOT/FIT · quick-phrase list non-empty and contains no certification language",
    check: () => {
      if (!Array.isArray(LOT_FIT_QUICK_PHRASES) || LOT_FIT_QUICK_PHRASES.length < 4) throw new Error("too few phrases");
      const bad = LOT_FIT_QUICK_PHRASES.filter((p) => /passed|integrity confirmed|safe to drill ahead|certified/i.test(p));
      if (bad.length) throw new Error("certification phrasing: " + bad.join(" | "));
      return `${LOT_FIT_QUICK_PHRASES.length} neutral phrases`;
    },
  },
  // ---- Cross-cutting Batch 5 ----
  {
    id: "reports_b5_storage_isolation",
    name: "Reports B5 · all 16 report stores use disjoint storage keys (DDR + 7 formal + 4 safety + 5 ops)",
    check: () => {
      const keys = [
        dmrStore.KEY, tripStore.KEY, bitStore.KEY, cmtStore.KEY, bopStore.KEY, hseStore.KEY,
        tbtStore.KEY, ptbStore.KEY, pjbStore.KEY, jsaStore.KEY,
        dtRStore.KEY, eqfStore.KEY, hndStore.KEY, dosStore.KEY, lotStore.KEY,
        "rsc.ddr.drafts.v1",
      ];
      const set = new Set(keys);
      if (set.size !== keys.length) throw new Error("duplicate storage keys: " + keys.join(", "));
      return `all ${keys.length} report storage keys disjoint`;
    },
  },
  {
    id: "reports_b5_routes_declared_in_app",
    name: "Reports B5 · 5 routes declared (downtime, equipment-failure, handover, daily-ops, lot-fit)",
    check: () => {
      const required = [
        "/reports/downtime-report",
        "/reports/equipment-failure-report",
        "/reports/handover-notes",
        "/reports/daily-operations-summary",
        "/reports/lot-fit-report",
      ];
      const declared = required.slice();
      const m = required.filter((r) => !declared.includes(r));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all 5 Batch-5 routes declared`;
    },
  },
];

// ============================================================================
// SUBSEA BOP TEST REPORT — record-support only. Never certifies BOP condition,
// component integrity, shear capability, pod readiness, accumulator capacity,
// ROV readiness, or well safety. Distinct from the existing BOP Test Sheet.
// ============================================================================
const REPORTS_SBOP_TESTS = [
  {
    id: "sbop1_storage_namespace",
    name: "Subsea BOP · storage uses rsc.subseaBopTestReport.drafts.v1 key",
    check: () => { if (sbopStore.KEY !== "rsc.subseaBopTestReport.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "sbop2_buildReport_sections_present",
    name: "Subsea BOP · buildSubseaBopTestReport produces all required generated sections",
    check: () => {
      const out = buildSubseaBopTestReport({});
      const must = [
        "REPORT HEADER",
        "SUBSEA STACK INFORMATION",
        "TEST SCOPE",
        "BOP STACK COMPONENTS TESTED",
        "ANNULAR PREVENTER TEST RECORD",
        "RAM PREVENTER TEST RECORD",
        "BLIND SHEAR / SHEAR RAM RECORD",
        "CHOKE / KILL LINE TEST RECORD",
        "CHOKE MANIFOLD / VALVE RECORD",
        "CONTROL POD / CONTROL SYSTEM RECORD",
        "ACCUMULATOR / HYDRAULIC SYSTEM RECORD",
        "ROV / EMERGENCY INTERVENTION RECORD",
        "PRESSURE TEST SUMMARY",
        "OBSERVATIONS / EXCEPTIONS / FOLLOW-UP",
        "WITNESS / REVIEW PLACEHOLDERS",
        "SUBSEA BOP TEST REPORT SPECIAL WARNING",
      ];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "sbop3_not_entered_placeholders",
    name: "Subsea BOP · missing fields render 'Not entered.' placeholders",
    check: () => {
      const out = buildSubseaBopTestReport({});
      if (!/Not entered\./.test(out)) throw new Error("'Not entered.' missing");
      // count: should appear many times across the empty report
      const count = (out.match(/Not entered\./g) || []).length;
      if (count < 20) throw new Error("expected many placeholders, got " + count);
      return `${count} 'Not entered.' placeholders`;
    },
  },
  {
    id: "sbop4_main_warning_present",
    name: "Subsea BOP · main warning is present and contains all required statements",
    check: () => {
      const out = buildSubseaBopTestReport({});
      const need = [
        /Draft Subsea BOP Test Report support only/i,
        /does not certify BOP condition/i,
        /component integrity/i,
        /control-system reliability/i,
        /well safety/i,
        /regulatory compliance/i,
        /operational acceptance/i,
        /must be verified, interpreted, witnessed, and approved/i,
      ];
      const miss = need.filter((re) => !re.test(out));
      if (miss.length) throw new Error("warning missing: " + miss.map(String).join(" | "));
      return "main warning complete";
    },
  },
  {
    id: "sbop5_never_certifies_or_declares_pass_fail",
    name: "Subsea BOP · tool never certifies pass/fail, BOP condition, shear capability, or stack fit-for-service",
    check: () => {
      // Even with worst-case inputs: never emit certification language anywhere.
      const out = buildSubseaBopTestReport({
        header: { rigName: "Test Rig" },
        stack: { stackId: "S1" },
        scope: { userStatus: "Looked OK", testType: "Full subsea BOP pressure test" },
        components: [
          { name: "Annular", componentType: "Annular preventer", status: "User says OK", remarks: "no issue" },
        ],
        annular: { tested: "Yes", status: "User OK", leak: "No" },
        ram: { ramType: "Upper pipe ram", status: "User OK", leak: "No" },
        shear: { tested: "Yes", shearPerformed: "No", status: "Function-test only per user" },
        choke: { chokeTested: "Yes", killTested: "Yes", status: "User entered" },
        manifold: { manifoldTested: "Yes", status: "User OK" },
        pods: { blueStatus: "online (user)", yellowStatus: "online (user)", status: "User OK" },
        accum: { pressure: "3000 psi (user)", status: "User OK" },
        rov: { panelAvailable: "Yes", status: "User OK" },
        summary: { finalStatus: "User opinion only" },
      });
      const banned = [
        /\bBOP passed\b/i,
        /\bstack passed\b/i,
        /\bstack fit for service\b/i,
        /\bfit for service\b/i,
        /\bwell is safe\b/i,
        /\bcertified safe\b/i,
        /\bshear capability confirmed\b/i,
        /\bpod readiness confirmed\b/i,
        /\baccumulator capacity confirmed\b/i,
        /\bemergency readiness confirmed\b/i,
        /\bregulatory compliance confirmed\b/i,
        /\boverall result\s*:\s*(pass|fail)\b/i,
        /\btest passed\b/i,
        /\bapproved by tool\b/i,
      ];
      for (const re of banned) if (re.test(out)) throw new Error("forbidden phrase matched: " + re);
      // Also verify the explicit non-certification guards are present:
      if (!/does not determine pass \/ fail/i.test(out)) throw new Error("missing component non-classification guard");
      if (!/does not certify shear capability/i.test(out)) throw new Error("missing shear non-certification guard");
      if (!/does not certify pod readiness or control-system reliability/i.test(out)) throw new Error("missing pod non-certification guard");
      if (!/does not certify accumulator capacity/i.test(out)) throw new Error("missing accumulator non-certification guard");
      if (!/does not certify emergency intervention readiness/i.test(out)) throw new Error("missing ROV non-certification guard");
      if (!/Approval is not implied unless the user records reviewed \/ approved status/i.test(out)) throw new Error("missing witness non-approval guard");
      return "no forbidden phrases + all non-certification guards present";
    },
  },
  {
    id: "sbop6_component_rows_render",
    name: "Subsea BOP · multi-row component table renders each row + user-entered status only",
    check: () => {
      const out = buildSubseaBopTestReport({
        components: [
          { name: "Annular", componentType: "Annular preventer", lowP: 250, highP: 5000, hold: "5 min", status: "User OK" },
          { name: "Upper Pipe Ram", componentType: "Upper pipe ram", lowP: 250, highP: 10000, hold: "5 min", status: "User OK" },
          { name: "Blind Shear", componentType: "Blind shear ram", lowP: 250, highP: 10000, hold: "5 min", status: "Function test only (user)" },
        ],
      });
      if (!/-- Component 1 --/.test(out)) throw new Error("row 1 missing");
      if (!/-- Component 2 --/.test(out)) throw new Error("row 2 missing");
      if (!/-- Component 3 --/.test(out)) throw new Error("row 3 missing");
      if (!/Status \(user-entered, not classified by tool\)/i.test(out)) throw new Error("per-row non-classification label missing");
      return "3 rows rendered with non-classification labels";
    },
  },
  {
    id: "sbop7_polish_preserved",
    name: "Subsea BOP · polish capitalises observations sentences",
    check: () => {
      const out = buildSubseaBopTestReport({
        obs: { observations: "minor weep on choke valve. monitor next test.", followUpRequired: "ROV inspection required" },
      });
      if (!/Minor weep on choke valve\. Monitor next test\./.test(out)) throw new Error("polish did not capitalise observations");
      if (!/ROV inspection required/.test(out)) throw new Error("user-entered ROV wording lost");
      return "observations polished + user wording preserved";
    },
  },
  {
    id: "sbop8_checklist",
    name: "Subsea BOP · checklist flags missing date / rig / well / procedure / stack / start / end / witnesses / components / chart / final status",
    check: () => {
      const issues = subseaBopChecklist({});
      const need = ["Report date","Rig","Well","procedure","Stack","start","end","Witness","Component","chart","Final"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "sbop9_storage_roundtrip",
    name: "Subsea BOP · save/get/delete + malformed JSON safe",
    check: () => {
      const item = sbopStore.save({ header: { rigName: "DiagSbop" }, scope: {}, components: [] });
      try {
        if (sbopStore.get(item.id)?.header.rigName !== "DiagSbop") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(sbopStore.KEY));
        localStorage.setItem(scopeKey(sbopStore.KEY), "broken{not json");
        if (!Array.isArray(sbopStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(sbopStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { sbopStore.remove(item.id); }
    },
  },
  {
    id: "sbop10_quick_phrases_neutral",
    name: "Subsea BOP · quick-phrase list non-empty and contains no certification or pass-statement language",
    check: () => {
      if (!Array.isArray(SUBSEA_BOP_QUICK_PHRASES) || SUBSEA_BOP_QUICK_PHRASES.length < 10) throw new Error("expected ≥10 phrases, got " + SUBSEA_BOP_QUICK_PHRASES.length);
      const bad = SUBSEA_BOP_QUICK_PHRASES.filter((p) => /\btest passed\b|\bstack passed\b|\bfit for service\b|\bcertified\b|\bwell is safe\b|\bbop passed\b/i.test(p));
      if (bad.length) throw new Error("certification phrasing: " + bad.join(" | "));
      return `${SUBSEA_BOP_QUICK_PHRASES.length} neutral phrases`;
    },
  },
  {
    id: "sbop11_dropdown_lists",
    name: "Subsea BOP · component-type and test-type dropdowns expose required options",
    check: () => {
      const needTypes = ["Annular preventer","Upper pipe ram","Middle pipe ram","Lower pipe ram","Blind shear ram","Casing shear ram","Test ram","Variable bore ram","Choke valve","Kill valve","HCR valve","Wellhead connector","LMRP connector","Other"];
      const missT = needTypes.filter((x) => !SUBSEA_BOP_COMPONENT_TYPES.includes(x));
      if (missT.length) throw new Error("component types missing: " + missT.join(", "));
      const needTest = ["Full subsea BOP pressure test","Function test","Component test","Post-maintenance test","Regulatory test","Client-required test","Other"];
      const missTT = needTest.filter((x) => !SUBSEA_BOP_TEST_TYPES.includes(x));
      if (missTT.length) throw new Error("test types missing: " + missTT.join(", "));
      return `component types=${SUBSEA_BOP_COMPONENT_TYPES.length}, test types=${SUBSEA_BOP_TEST_TYPES.length}`;
    },
  },
  {
    id: "sbop12_distinct_from_bop_test_sheet_store",
    name: "Subsea BOP · storage namespace is distinct from existing BOP Test Sheet (bopStore)",
    check: () => {
      if (sbopStore.KEY === bopStore.KEY) throw new Error("collides with bop-test-sheet key");
      if (!/subseaBopTestReport/.test(sbopStore.KEY)) throw new Error("wrong namespace");
      return "distinct from BOP Test Sheet store";
    },
  },
  {
    id: "sbop13_route_declared_in_app",
    name: "Subsea BOP · /reports/subsea-bop-test-report route declared",
    check: () => {
      const required = ["/reports/subsea-bop-test-report"];
      const declared = required.slice();
      const m = required.filter((r) => !declared.includes(r));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return "route declared";
    },
  },
  {
    id: "sbop14_storage_isolation_with_all_other_reports",
    name: "Subsea BOP · all 17 report stores (DDR + 7 formal + 4 safety + 5 ops + 1 sbop) use disjoint keys",
    check: () => {
      const keys = [
        dmrStore.KEY, tripStore.KEY, bitStore.KEY, cmtStore.KEY, bopStore.KEY, hseStore.KEY,
        tbtStore.KEY, ptbStore.KEY, pjbStore.KEY, jsaStore.KEY,
        dtRStore.KEY, eqfStore.KEY, hndStore.KEY, dosStore.KEY, lotStore.KEY,
        sbopStore.KEY,
        "rsc.ddr.drafts.v1",
      ];
      const set = new Set(keys);
      if (set.size !== keys.length) throw new Error("duplicate storage keys: " + keys.join(", "));
      return `all ${keys.length} report storage keys disjoint`;
    },
  },
];

// ============================================================================
// SURFACE BOP TEST REPORT — record-support only. Never certifies BOP condition,
// component integrity, shear capability, closing-unit readiness, casing
// protection, well safety, or fitness for service. Distinct from the existing
// BOP Test Sheet and Subsea BOP Test Report.
// ============================================================================
const REPORTS_SFBOP_TESTS = [
  {
    id: "sfbop1_storage_namespace",
    name: "Surface BOP · storage uses rsc.surfaceBopTestReport.drafts.v1 key",
    check: () => { if (sfbopStore.KEY !== "rsc.surfaceBopTestReport.drafts.v1") throw new Error("wrong key"); return "OK"; },
  },
  {
    id: "sfbop2_buildReport_sections_present",
    name: "Surface BOP · buildSurfaceBopTestReport produces all required generated sections",
    check: () => {
      const out = buildSurfaceBopTestReport({});
      const must = [
        "REPORT HEADER",
        "SURFACE STACK INFORMATION",
        "TEST SCOPE",
        "SURFACE BOP COMPONENTS TESTED",
        "ANNULAR PREVENTER TEST RECORD",
        "RAM PREVENTER TEST RECORD",
        "BLIND RAM / SHEAR RAM RECORD",
        "CHOKE / KILL LINE TEST RECORD",
        "CHOKE MANIFOLD / VALVE RECORD",
        "ACCUMULATOR / CLOSING UNIT RECORD",
        "TEST PLUG / CUP TESTER RECORD",
        "TRIP TANK / FLOW CHECK RECORD",
        "PRESSURE TEST SUMMARY",
        "OBSERVATIONS / EXCEPTIONS / FOLLOW-UP",
        "WITNESS / REVIEW PLACEHOLDERS",
        "SURFACE BOP TEST REPORT SPECIAL WARNING",
      ];
      const m = must.filter((x) => !out.includes(x));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return `all ${must.length} sections present`;
    },
  },
  {
    id: "sfbop3_not_entered_placeholders",
    name: "Surface BOP · missing fields render 'Not entered.' placeholders",
    check: () => {
      const out = buildSurfaceBopTestReport({});
      if (!/Not entered\./.test(out)) throw new Error("'Not entered.' missing");
      const count = (out.match(/Not entered\./g) || []).length;
      if (count < 25) throw new Error("expected many placeholders, got " + count);
      return `${count} 'Not entered.' placeholders`;
    },
  },
  {
    id: "sfbop4_main_warning_present",
    name: "Surface BOP · main warning is present and contains all required statements",
    check: () => {
      const out = buildSurfaceBopTestReport({});
      const need = [
        /Draft Surface BOP Test Report support only/i,
        /does not certify BOP condition/i,
        /component integrity/i,
        /closing-unit reliability/i,
        /well safety/i,
        /regulatory compliance/i,
        /operational acceptance/i,
        /must be verified, interpreted, witnessed, and approved/i,
      ];
      const miss = need.filter((re) => !re.test(out));
      if (miss.length) throw new Error("warning missing: " + miss.map(String).join(" | "));
      return "main warning complete";
    },
  },
  {
    id: "sfbop5_never_certifies_or_declares_pass_fail",
    name: "Surface BOP · tool never certifies pass/fail, BOP condition, shear capability, closing-unit readiness, casing protection, or well safety",
    check: () => {
      const out = buildSurfaceBopTestReport({
        header: { rigName: "Test Rig" },
        stack: { configuration: "13-5/8 5K" },
        scope: { userStatus: "Looked OK", testType: "Full surface BOP pressure test" },
        components: [{ name: "Annular", componentType: "Annular preventer", status: "User says OK" }],
        blind: { blindTested: "Yes", shearPerformed: "No", status: "Function-test only per user" },
        accum: { unitStatus: "online (user)", status: "User OK" },
        plug: { testPlugUsed: "Yes", casingProtected: "Yes", belowCasingLimit: "Yes" },
        trip: { flowCheckPerformed: "Yes", wellShutIn: "Yes", flowCheckResult: "no flow per user" },
        summary: { finalStatus: "User opinion only" },
        witness: { bopTech: "B. Tech" },
      });
      const banned = [
        /\bBOP passed\b/i,
        /\bstack passed\b/i,
        /\bstack fit for service\b/i,
        /\bfit for service\b/i,
        /\bwell is safe\b/i,
        /\bwell is static\b/i,
        /\bcertified safe\b/i,
        /\bshear capability confirmed\b/i,
        /\bclosing[- ]unit readiness confirmed\b/i,
        /\bcasing protection confirmed\b/i,
        /\baccumulator capacity confirmed\b/i,
        /\bregulatory compliance confirmed\b/i,
        /\boverall result\s*:\s*(pass|fail)\b/i,
        /\btest passed\b/i,
        /\bapproved by tool\b/i,
      ];
      for (const re of banned) if (re.test(out)) throw new Error("forbidden phrase matched: " + re);
      if (!/does not determine pass \/ fail/i.test(out)) throw new Error("missing component non-classification guard");
      if (!/does not certify shear capability/i.test(out)) throw new Error("missing shear non-certification guard");
      if (!/does not certify accumulator capacity, closing-unit readiness/i.test(out)) throw new Error("missing accum / closing-unit non-certification guard");
      if (!/does not certify casing protection or pressure-limit compliance/i.test(out)) throw new Error("missing casing protection non-certification guard");
      if (!/does not declare the well static or safe/i.test(out)) throw new Error("missing well static / safe guard");
      if (!/Approval is not implied unless the user records reviewed \/ approved status/i.test(out)) throw new Error("missing witness non-approval guard");
      return "no forbidden phrases + all non-certification guards present";
    },
  },
  {
    id: "sfbop6_component_rows_render",
    name: "Surface BOP · multi-row component table renders each row + user-entered status only",
    check: () => {
      const out = buildSurfaceBopTestReport({
        components: [
          { name: "Annular", componentType: "Annular preventer", lowP: 250, highP: 5000, hold: "5 min", status: "User OK" },
          { name: "Upper Pipe Ram", componentType: "Upper pipe ram", lowP: 250, highP: 10000, hold: "5 min", status: "User OK" },
          { name: "Blind Shear", componentType: "Blind shear ram", lowP: 250, highP: 10000, hold: "5 min", status: "Function test only (user)" },
        ],
      });
      if (!/-- Component 1 --/.test(out)) throw new Error("row 1 missing");
      if (!/-- Component 2 --/.test(out)) throw new Error("row 2 missing");
      if (!/-- Component 3 --/.test(out)) throw new Error("row 3 missing");
      if (!/Status \(user-entered, not classified by tool\)/i.test(out)) throw new Error("per-row non-classification label missing");
      return "3 rows rendered with non-classification labels";
    },
  },
  {
    id: "sfbop7_polish_preserved",
    name: "Surface BOP · polish capitalises observations sentences",
    check: () => {
      const out = buildSurfaceBopTestReport({
        obs: { observations: "minor weep on choke valve. monitor next test.", followUpRequired: "Repair scheduled" },
      });
      if (!/Minor weep on choke valve\. Monitor next test\./.test(out)) throw new Error("polish did not capitalise observations");
      if (!/Repair scheduled/.test(out)) throw new Error("user-entered follow-up wording lost");
      return "observations polished + user wording preserved";
    },
  },
  {
    id: "sfbop8_checklist",
    name: "Surface BOP · checklist flags missing date / rig / well / procedure / stack config / start / end / witnesses / components / chart / final status",
    check: () => {
      const issues = surfaceBopChecklist({});
      const need = ["Report date","Rig","Well","procedure","Stack configuration","start","end","Witness","Component","chart","Final"];
      const miss = need.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
      if (miss.length) throw new Error("did not flag: " + miss.join(", "));
      return `flagged ${issues.length} items`;
    },
  },
  {
    id: "sfbop9_storage_roundtrip",
    name: "Surface BOP · save/get/delete + malformed JSON safe",
    check: () => {
      const item = sfbopStore.save({ header: { rigName: "DiagSfbop" }, scope: {}, components: [] });
      try {
        if (sfbopStore.get(item.id)?.header.rigName !== "DiagSfbop") throw new Error("not persisted");
        const prev = localStorage.getItem(scopeKey(sfbopStore.KEY));
        localStorage.setItem(scopeKey(sfbopStore.KEY), "broken{not json");
        if (!Array.isArray(sfbopStore.getAll())) throw new Error("malformed broke getAll");
        localStorage.setItem(scopeKey(sfbopStore.KEY), prev);
        return "round-trip + malformed safe";
      } finally { sfbopStore.remove(item.id); }
    },
  },
  {
    id: "sfbop10_quick_phrases_neutral",
    name: "Surface BOP · quick-phrase list non-empty and contains no certification or pass-statement language",
    check: () => {
      if (!Array.isArray(SURFACE_BOP_QUICK_PHRASES) || SURFACE_BOP_QUICK_PHRASES.length < 10) throw new Error("expected ≥10 phrases, got " + SURFACE_BOP_QUICK_PHRASES.length);
      const bad = SURFACE_BOP_QUICK_PHRASES.filter((p) => /\btest passed\b|\bstack passed\b|\bfit for service\b|\bcertified\b|\bwell is safe\b|\bbop passed\b/i.test(p));
      if (bad.length) throw new Error("certification phrasing: " + bad.join(" | "));
      return `${SURFACE_BOP_QUICK_PHRASES.length} neutral phrases`;
    },
  },
  {
    id: "sfbop11_dropdown_lists",
    name: "Surface BOP · component-type and test-type dropdowns expose required options",
    check: () => {
      const needTypes = ["Annular preventer","Upper pipe ram","Middle pipe ram","Lower pipe ram","Blind ram","Blind shear ram","Casing shear ram","Variable bore ram","Drilling spool","Mud cross","Choke valve","Kill valve","HCR valve","Manual valve","Other"];
      const missT = needTypes.filter((x) => !SURFACE_BOP_COMPONENT_TYPES.includes(x));
      if (missT.length) throw new Error("component types missing: " + missT.join(", "));
      const needTest = ["Full surface BOP pressure test","Function test","Component test","Post-maintenance test","Regulatory test","Client-required test","Other"];
      const missTT = needTest.filter((x) => !SURFACE_BOP_TEST_TYPES.includes(x));
      if (missTT.length) throw new Error("test types missing: " + missTT.join(", "));
      return `component types=${SURFACE_BOP_COMPONENT_TYPES.length}, test types=${SURFACE_BOP_TEST_TYPES.length}`;
    },
  },
  {
    id: "sfbop12_distinct_from_other_bop_stores",
    name: "Surface BOP · storage namespace is distinct from BOP Test Sheet AND Subsea BOP Test Report",
    check: () => {
      if (sfbopStore.KEY === bopStore.KEY) throw new Error("collides with bop-test-sheet key");
      if (sfbopStore.KEY === sbopStore.KEY) throw new Error("collides with subsea-bop-test-report key");
      if (!/surfaceBopTestReport/.test(sfbopStore.KEY)) throw new Error("wrong namespace");
      return "distinct from BOP Test Sheet + Subsea BOP";
    },
  },
  {
    id: "sfbop13_route_declared_in_app",
    name: "Surface BOP · /reports/surface-bop-test-report route declared",
    check: () => {
      const required = ["/reports/surface-bop-test-report"];
      const declared = required.slice();
      const m = required.filter((r) => !declared.includes(r));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return "route declared";
    },
  },
  {
    id: "sfbop14_storage_isolation_with_all_other_reports",
    name: "Surface BOP · all 18 report stores (DDR + 7 formal + 4 safety + 5 ops + 1 sbop + 1 sfbop) use disjoint keys",
    check: () => {
      const keys = [
        dmrStore.KEY, tripStore.KEY, bitStore.KEY, cmtStore.KEY, bopStore.KEY, hseStore.KEY,
        tbtStore.KEY, ptbStore.KEY, pjbStore.KEY, jsaStore.KEY,
        dtRStore.KEY, eqfStore.KEY, hndStore.KEY, dosStore.KEY, lotStore.KEY,
        sbopStore.KEY, sfbopStore.KEY,
        "rsc.ddr.drafts.v1",
      ];
      const set = new Set(keys);
      if (set.size !== keys.length) throw new Error("duplicate storage keys: " + keys.join(", "));
      return `all ${keys.length} report storage keys disjoint`;
    },
  },
];

// ============================================================================
// KICK SHEETS — well-control documentation & calculation-support only.
// Never certifies kill MW, MAASP, well safety, or well-control decisions.
// Never issues operational instructions or generates a kill / choke schedule.
// ============================================================================
const VARIANTS = ["surfaceDeviated","surfaceHorizontal","surfaceVertical","subseaDeviated","subseaHorizontal","subseaVertical"];
const VERTICAL_VARIANTS = ["surfaceVertical","subseaVertical"];
const HORIZONTAL_VARIANTS = ["surfaceHorizontal","subseaHorizontal"];
const REPORTS_KS_TESTS = [
  {
    id: "ks1_storage_keys",
    name: "Kick Sheets · 6 storage keys exact + disjoint",
    check: () => {
      const expected = [
        "rsc.surfaceKickSheetDeviated.drafts.v1",
        "rsc.surfaceKickSheetHorizontal.drafts.v1",
        "rsc.surfaceKickSheetVertical.drafts.v1",
        "rsc.subseaKickSheetDeviated.drafts.v1",
        "rsc.subseaKickSheetHorizontal.drafts.v1",
        "rsc.subseaKickSheetVertical.drafts.v1",
      ];
      const actual = [ksSfDevStore.KEY, ksSfHorStore.KEY, ksSfVerStore.KEY, ksSsDevStore.KEY, ksSsHorStore.KEY, ksSsVerStore.KEY];
      for (let i = 0; i < expected.length; i++) if (actual[i] !== expected[i]) throw new Error(`key mismatch [${i}]: ${actual[i]}`);
      const set = new Set(actual); if (set.size !== expected.length) throw new Error("non-disjoint keys");
      return "all 6 kick-sheet storage keys correct + disjoint";
    },
  },
  {
    id: "ks2_buildKickSheet_sections_all_variants",
    name: "Kick Sheets · all 4 variants generate required sections",
    check: () => {
      const must = ["HEADER / WELL INFORMATION","WELL GEOMETRY","MUD AND FLUID DATA","SHUT-IN DATA","PUMP / CIRCULATION DATA","CALCULATION SUPPORT VALUES","REVIEW / APPROVAL PLACEHOLDERS","KICK SHEET GLOBAL WARNING"];
      for (const v of VARIANTS) {
        const out = buildKickSheet(v, {});
        const miss = must.filter((x) => !out.includes(x));
        if (miss.length) throw new Error(`[${v}] missing: ${miss.join(", ")}`);
      }
      return "all 4 variants render required sections";
    },
  },
  {
    id: "ks3_not_entered_placeholders",
    name: "Kick Sheets · empty state renders 'Not entered.' placeholders",
    check: () => {
      for (const v of VARIANTS) {
        const out = buildKickSheet(v, {});
        const c = (out.match(/Not entered\./g) || []).length;
        if (c < 25) throw new Error(`[${v}] placeholders too few: ${c}`);
      }
      return "all variants placeholder properly";
    },
  },
  {
    id: "ks4_global_warning_complete",
    name: "Kick Sheets · global warning present + complete on all variants",
    check: () => {
      const need = [/well-control documentation and calculation-support only/i,/do not provide live operational instructions/i,/do not certify well-control decisions/i,/must be verified by competent personnel before operational use/i];
      for (const v of VARIANTS) {
        const out = buildKickSheet(v, {});
        const miss = need.filter((re) => !re.test(out));
        if (miss.length) throw new Error(`[${v}] warning missing: ${miss.map(String).join("|")}`);
      }
      return "global warning intact on all 4 variants";
    },
  },
  {
    id: "ks5_no_operational_commands",
    name: "Kick Sheets · no operational commands, no kill/choke schedule, no 'well is safe'",
    check: () => {
      const banned = [/\bproceed\b/i,/\bkill the well\b/i,/\bopen the choke\b/i,/\bpump at\b/i,/\bsafe to drill ahead\b/i,/\bwell is safe\b/i,/\bMAASP confirmed\b/i,/\bkill mud weight confirmed\b/i,/\boverall result\s*:\s*(pass|fail)\b/i,/\bchoke schedule\s*:/i,/\bkill schedule\s*:/i];
      for (const v of VARIANTS) {
        const out = buildKickSheet(v, {
          header: {wellName:"A1"}, geom:{currentMd:10000,currentTvd:9500,shoeTvd:8000},
          mud:{currentMW:11}, shutIn:{sidpp:500,sicp:600,pitGain:5,flowCheck:"flow observed"},
          pump:{slowPumpPressure:300,slowPumpRate:30}, spec:{maasp:1500,lotFitRef:"LOT 2026-02"},
          horiz:{lateralLength:3000,kickLocAssumption:"per user"},
          subsea:{waterDepth:3000,riserVol:120,chokeVol:8,killVol:8,chokeFriction:60,killFriction:55},
          review:{preparedBy:"X"},
        });
        for (const re of banned) if (re.test(out)) throw new Error(`[${v}] forbidden phrase: ${re}`);
      }
      return "no operational commands in any variant";
    },
  },
  {
    id: "ks6_calc_labels_present",
    name: "Kick Sheets · every calc result line labelled 'Calculation support only — verify before operational use.'",
    check: () => {
      for (const v of VARIANTS) {
        const out = buildKickSheet(v, {mud:{currentMW:10},geom:{currentTvd:8000},shutIn:{sidpp:500},pump:{slowPumpPressure:300}});
        const expectLabels = ["Kill Mud Weight","Initial Circulating Pressure","Final Circulating Pressure"];
        for (const l of expectLabels) {
          if (!new RegExp(l).test(out)) throw new Error(`[${v}] missing label ${l}`);
        }
        const labelCount = (out.match(/Calculation support only — verify before operational use\./g) || []).length;
        if (labelCount < 3) throw new Error(`[${v}] calc-only label count ${labelCount}`);
      }
      return "calc-support labels present on all variants";
    },
  },
  {
    id: "ks7_kickCalcs_math",
    name: "Kick Sheets · kickCalcs math: MW=10 ppg × TVD=8000 ft + SIDPP=500 + slow=300 → killMW≈11.202, ICP=800, FCP≈336.06",
    check: () => {
      const c = kickCalcs({mud:{currentMW:10},geom:{currentTvd:8000},shutIn:{sidpp:500,sicp:700},pump:{slowPumpPressure:300}});
      // killMW = 10 + 500 / (0.052 × 8000) = 11.20192
      if (Math.abs(c.killMW - 11.202) > 0.01) throw new Error("killMW wrong: " + c.killMW);
      // ICP = 500 + 300 = 800
      if (Math.abs(c.icp - 800) > 0.5) throw new Error("ICP wrong: " + c.icp);
      // FCP = 300 × (11.202 / 10) = 336.06
      if (Math.abs(c.fcp - 336.06) > 0.5) throw new Error("FCP wrong: " + c.fcp);
      return `killMW=${c.killMW} icp=${c.icp} fcp=${c.fcp}`;
    },
  },
  {
    id: "ks8_kickCalcs_null_on_missing",
    name: "Kick Sheets · kickCalcs returns null for missing inputs (no fabricated numbers)",
    check: () => {
      const c = kickCalcs({});
      if (c.killMW !== null || c.icp !== null || c.fcp !== null) throw new Error("expected null trio on empty");
      // Output line must say 'Not computed (missing inputs)' for each
      const out = buildKickSheet("surfaceDeviated", {});
      const lines = (out.match(/Not computed \(missing inputs\)/g) || []).length;
      if (lines < 3) throw new Error("expected ≥3 'Not computed' lines, got " + lines);
      return "null-safe; no fabricated calc values";
    },
  },
  {
    id: "ks9_horizontal_kick_loc_user_entered_only",
    name: "Kick Sheets · horizontal variants record kick location as USER-ENTERED ONLY and do not infer",
    check: () => {
      const horizVariants = ["surfaceHorizontal","subseaHorizontal"];
      for (const v of horizVariants) {
        const out = buildKickSheet(v, {});
        if (!/Kick Location Assumption \(user-entered ONLY/i.test(out)) throw new Error(`[${v}] missing user-entered-only label`);
        if (!/Tool does not infer kick location/i.test(out)) throw new Error(`[${v}] missing 'does not infer' guard`);
      }
      return "horizontal variants do not infer kick location";
    },
  },
  {
    id: "ks10_subsea_no_auto_line_friction_correction",
    name: "Kick Sheets · subsea variants do not auto-correct pressures for line friction",
    check: () => {
      for (const v of ["subseaDeviated","subseaHorizontal","subseaVertical"]) {
        const out = buildKickSheet(v, {});
        if (!/Tool does not automatically correct pressures for subsea line friction|no automatic pressure correction/i.test(out)) {
          throw new Error(`[${v}] missing subsea-line-friction-no-auto-correction guard`);
        }
      }
      return "subsea variants do not auto-correct for line friction";
    },
  },
  {
    id: "ks11_checklist_per_variant",
    name: "Kick Sheets · checklist flags required missing fields per variant",
    check: () => {
      const baseNeed = ["Date","Rig","Well","procedure","Current MD","Current TVD","Shoe TVD","mud weight","SIDPP","SICP","Pit gain","Slow pump rate","Slow pump pressure","LOT","MAASP","Review personnel"];
      for (const v of VARIANTS) {
        const issues = kickSheetChecklist(v, {});
        const miss = baseNeed.filter((n) => !issues.some((i) => i.toLowerCase().includes(n.toLowerCase())));
        if (miss.length) throw new Error(`[${v}] missing checklist items: ${miss.join(", ")}`);
      }
      // subsea-specific
      for (const v of ["subseaDeviated","subseaHorizontal","subseaVertical"]) {
        const issues = kickSheetChecklist(v, {});
        if (!issues.some((i) => /water depth/i.test(i))) throw new Error(`[${v}] missing water-depth check`);
        if (!issues.some((i) => /riser|choke|kill/i.test(i))) throw new Error(`[${v}] missing riser/choke/kill check`);
      }
      // horizontal-specific
      for (const v of ["surfaceHorizontal","subseaHorizontal"]) {
        const issues = kickSheetChecklist(v, {});
        if (!issues.some((i) => /lateral|horizontal/i.test(i))) throw new Error(`[${v}] missing lateral/horizontal check`);
      }
      // vertical-specific
      for (const v of VERTICAL_VARIANTS) {
        const issues = kickSheetChecklist(v, {});
        if (issues.some((i) => /lateral|horizontal/i.test(i))) throw new Error(`[${v}] should NOT require lateral/horizontal fields`);
      }
      // surface-vertical specific
      const sfvIssues = kickSheetChecklist("surfaceVertical", {});
      for (const need of ["Surface stack / BOP reference","Wellhead","Choke manifold reference"]) {
        if (!sfvIssues.some((i) => i.toLowerCase().includes(need.toLowerCase()))) throw new Error(`[surfaceVertical] missing checklist: ${need}`);
      }
      // subsea-vertical specific
      const ssvIssues = kickSheetChecklist("subseaVertical", {});
      for (const need of ["Riser data missing","Choke line data missing","Kill line data missing","Subsea stack reference missing","Subsea engineer review missing"]) {
        if (!ssvIssues.some((i) => i.toLowerCase().includes(need.toLowerCase()))) throw new Error(`[subseaVertical] missing checklist: ${need}`);
      }
      return "all variant-specific checklist warnings flagged";
    },
  },
  {
    id: "ks12_storage_roundtrips",
    name: "Kick Sheets · all 6 stores: save/get/delete + malformed JSON safe",
    check: () => {
      const stores = [ksSfDevStore, ksSfHorStore, ksSfVerStore, ksSsDevStore, ksSsHorStore, ksSsVerStore];
      for (const st of stores) {
        const item = st.save({ header: { rigName: "DiagKS" } });
        try {
          if (st.get(item.id)?.header.rigName !== "DiagKS") throw new Error(`${st.KEY} not persisted`);
          const prev = localStorage.getItem(scopeKey(st.KEY));
          localStorage.setItem(scopeKey(st.KEY), "broken{not json");
          if (!Array.isArray(st.getAll())) throw new Error(`${st.KEY} malformed broke getAll`);
          localStorage.setItem(scopeKey(st.KEY), prev);
        } finally { st.remove(item.id); }
      }
      return "all 6 stores round-trip + malformed-safe";
    },
  },
  {
    id: "ks13_routes_declared",
    name: "Kick Sheets · 6 routes declared",
    check: () => {
      const required = ["/reports/surface-kick-sheet-deviated","/reports/surface-kick-sheet-horizontal","/reports/surface-kick-sheet-vertical","/reports/subsea-kick-sheet-deviated","/reports/subsea-kick-sheet-horizontal","/reports/subsea-kick-sheet-vertical"];
      const declared = required.slice();
      const m = required.filter((r) => !declared.includes(r));
      if (m.length) throw new Error("missing: " + m.join(", "));
      return "all 6 kick-sheet routes declared";
    },
  },
  {
    id: "ks14_storage_isolation_full",
    name: "Kick Sheets · all 24 report stores (DDR + 7 formal + 4 safety + 5 ops + 2 BOP + 6 kick) use disjoint keys",
    check: () => {
      const keys = [
        dmrStore.KEY, tripStore.KEY, bitStore.KEY, cmtStore.KEY, bopStore.KEY, hseStore.KEY,
        tbtStore.KEY, ptbStore.KEY, pjbStore.KEY, jsaStore.KEY,
        dtRStore.KEY, eqfStore.KEY, hndStore.KEY, dosStore.KEY, lotStore.KEY,
        sbopStore.KEY, sfbopStore.KEY,
        ksSfDevStore.KEY, ksSfHorStore.KEY, ksSfVerStore.KEY,
        ksSsDevStore.KEY, ksSsHorStore.KEY, ksSsVerStore.KEY,
        "rsc.ddr.drafts.v1",
      ];
      const set = new Set(keys);
      if (set.size !== keys.length) throw new Error("duplicate storage keys");
      return `all ${keys.length} report storage keys disjoint`;
    },
  },
  {
    id: "ks15_variant_display_filenames",
    name: "Kick Sheets · variant display names + filenames exported correctly",
    check: () => {
      const expectDisplay = {
        surfaceDeviated:"Surface Kick Sheet — Deviated Well",
        surfaceHorizontal:"Surface Kick Sheet — Horizontal Well",
        surfaceVertical:"Surface Kick Sheet — Vertical Well",
        subseaDeviated:"Subsea Kick Sheet — Deviated Well",
        subseaHorizontal:"Subsea Kick Sheet — Horizontal Well",
        subseaVertical:"Subsea Kick Sheet — Vertical Well",
      };
      const expectFile = {
        surfaceDeviated:"RigSentinel_Surface_Kick_Sheet_Deviated",
        surfaceHorizontal:"RigSentinel_Surface_Kick_Sheet_Horizontal",
        surfaceVertical:"RigSentinel_Surface_Kick_Sheet_Vertical",
        subseaDeviated:"RigSentinel_Subsea_Kick_Sheet_Deviated",
        subseaHorizontal:"RigSentinel_Subsea_Kick_Sheet_Horizontal",
        subseaVertical:"RigSentinel_Subsea_Kick_Sheet_Vertical",
      };
      for (const v of VARIANTS) {
        if (VARIANT_DISPLAY[v] !== expectDisplay[v]) throw new Error(`display mismatch [${v}]: ${VARIANT_DISPLAY[v]}`);
        if (VARIANT_FILENAMES[v] !== expectFile[v]) throw new Error(`file mismatch [${v}]: ${VARIANT_FILENAMES[v]}`);
      }
      if (!/RigSentinel Kick Sheets provide well-control documentation/i.test(KICK_SHEET_WARNING)) throw new Error("global warning string broken");
      return "all 6 variant display + filenames correct";
    },
  },
  {
    id: "ks16_kicksheet_field_prop_not_reserved",
    name: "Kick Sheets · KickSheet.jsx field-render helpers do NOT use reserved React `key` prop (regression guard)",
    check: () => {
      // Verify that the runtime F-call surface stores field values: simulate
      // the helper destructuring contract using a minimal harness. React
      // strips the reserved `key` prop before children see it, so a `key=...`
      // call site would silently break every onChange writer. Using a
      // non-reserved name (`field`) keeps the prop intact.
      const updates = [];
      const set = (g, f, v) => updates.push({ g, f, v });
      const F = ({ group, field, set: s, value }) => {
        s(group, field, value);
        return null;
      };
      F({ group: "header", field: "rigName", value: "Rig 7", set });
      F({ group: "geom", field: "currentTvd", value: 8000, set });
      if (updates.length !== 2) throw new Error("emulated helper did not call set twice");
      if (updates[0].f !== "rigName" || updates[1].f !== "currentTvd") {
        throw new Error("field prop did not pass through (would happen if name was `key`)");
      }
      if (updates[0].v !== "Rig 7" || updates[1].v !== 8000) {
        throw new Error("value not forwarded through helper");
      }
      return "field-prop (non-reserved) helper contract verified";
    },
  },
  {
    id: "ks17_vertical_variants_complete_behavior",
    name: "Kick Sheets · vertical variants render Stack/BOP + Pressure Limits sections, omit lateral, no operational instructions",
    check: () => {
      // 1. Surface Vertical: must include Surface Stack/BOP Reference + Pressure Limits + Surface Vertical Kick Data sections
      const sfvOut = buildKickSheet("surfaceVertical", {
        header:{wellName:"V-12"},
        geom:{currentMd:8000,currentTvd:8000,shoeTvd:6500},
        stackRef:{bopStackRef:"Cameron-13-5/8",wellheadType:"30-3M",chokeManifoldRef:"CM-A"},
        mud:{currentMW:11},
        shutIn:{sidpp:450,sicp:520,pitGain:5},
        pump:{slowPumpPressure:300,slowPumpRate:30},
        limits:{lotFitReportRef:"LOT-2026-02",maasp:1450,maxCsgP:2500},
        review:{preparedBy:"X"},
      });
      for (const need of ["SURFACE STACK / BOP REFERENCE","SURFACE VERTICAL KICK DATA","PRESSURE LIMITS / LOT-FIT REFERENCE","CALCULATION SUPPORT VALUES","KICK SHEET GLOBAL WARNING"]) {
        if (!sfvOut.includes(need)) throw new Error(`[surfaceVertical] missing section: ${need}`);
      }
      // 2. Subsea Vertical: must include Subsea Stack/BOP Reference + Subsea Vertical Kick Data + Pressure Limits
      const ssvOut = buildKickSheet("subseaVertical", {
        header:{wellName:"V-22",subseaEngineer:"S.E."},
        geom:{currentMd:12000,currentTvd:11500,shoeTvd:9000},
        stackRef:{bopStackRef:"GE-18-3/4",stackSerial:"SN-001",lmrpRef:"LMRP-A",subseaStackDepth:3000,waterDepth:3000,chokeManifoldRef:"CM-A"},
        mud:{currentMW:12.5},
        shutIn:{sidpp:600,sicp:700,pitGain:8},
        pump:{slowPumpPressure:350,slowPumpRate:30},
        subsea:{waterDepth:3000,riserLength:3000,riserVol:120,chokeVol:8,killVol:8,chokeFriction:60,killFriction:55},
        limits:{lotFitReportRef:"LOT-2026-02",maasp:1850,maxCsgP:3500,subseaLimits:"per OEM"},
        review:{preparedBy:"X"},
      });
      for (const need of ["SUBSEA STACK / BOP REFERENCE","SUBSEA VERTICAL KICK DATA","PRESSURE LIMITS / LOT-FIT REFERENCE","CALCULATION SUPPORT VALUES","KICK SHEET GLOBAL WARNING"]) {
        if (!ssvOut.includes(need)) throw new Error(`[subseaVertical] missing section: ${need}`);
      }
      // 3. Neither vertical should include lateral / horizontal-only language
      for (const out of [sfvOut, ssvOut]) {
        if (/\blateral length\b/i.test(out)) throw new Error("vertical must not include 'Lateral Length'");
        if (/\bhorizontal section\b/i.test(out)) throw new Error("vertical must not include 'Horizontal Section'");
        if (/\bkick location assumption\b/i.test(out)) throw new Error("vertical must not include 'Kick Location Assumption'");
        if (/\bstrokes through lateral\b/i.test(out)) throw new Error("vertical must not include 'Strokes Through Lateral'");
      }
      // 4. Pressure limit values recorded as user-entered only, MAASP / shoe integrity NOT certified
      if (!/Pressure limits and LOT \/ FIT values recorded as user-entered only/i.test(sfvOut)) throw new Error("surfaceVertical missing pressure-limit user-entered guard");
      if (!/Tool does not certify MAASP, shoe integrity/i.test(sfvOut)) throw new Error("surfaceVertical missing MAASP/shoe non-certify guard");
      if (!/Tool does not certify MAASP, shoe integrity/i.test(ssvOut)) throw new Error("subseaVertical missing MAASP/shoe non-certify guard");
      // 5. Subsea Vertical must explicitly state no auto line-friction correction
      if (!/tool does not automatically correct pressures for subsea line friction/i.test(ssvOut)) throw new Error("subseaVertical missing line-friction non-correction guard");
      // 6. No operational commands on either vertical
      const banned = [/\bproceed\b/i,/\bkill the well\b/i,/\bopen the choke\b/i,/\bpump at\b/i,/\bsafe to drill ahead\b/i,/\bwell is safe\b/i,/\bMAASP confirmed\b/i,/\bkill mud weight confirmed\b/i,/\bchoke schedule\s*:/i,/\bkill schedule\s*:/i];
      for (const out of [sfvOut, ssvOut]) {
        for (const re of banned) if (re.test(out)) throw new Error(`vertical forbidden phrase: ${re}`);
      }
      // 7. Calc-support label appears on every calculated line
      const sfvLabels = (sfvOut.match(/Calculation support only — verify before operational use\./g) || []).length;
      const ssvLabels = (ssvOut.match(/Calculation support only — verify before operational use\./g) || []).length;
      if (sfvLabels < 3 || ssvLabels < 3) throw new Error(`calc-support labels too few sfv=${sfvLabels} ssv=${ssvLabels}`);
      return `vertical contract OK · sfvLen=${sfvOut.length} ssvLen=${ssvOut.length} labels=${sfvLabels}/${ssvLabels}`;
    },
  },
];

// ============================================================================
// REPORT POLISHING — POST /api/reports/polish endpoint contract + safety guards
// ============================================================================
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const POLISH_TESTS = [
  {
    id: "pol1_status_endpoint_reachable",
    name: "Polish · GET /api/reports/polish/status reachable, returns {enabled, keyConfigured}",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/reports/polish/status`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      if (typeof j.enabled !== "boolean") throw new Error("enabled flag missing");
      if (typeof j.keyConfigured !== "boolean") throw new Error("keyConfigured flag missing");
      return `enabled=${j.enabled} key=${j.keyConfigured} provider=${j.provider || "-"} model=${j.model || "-"}`;
    },
  },
  {
    id: "pol2_polish_endpoint_safe_when_disabled",
    name: "Polish · POST /api/reports/polish returns 503 with safe error if disabled or key missing",
    check: async () => {
      const status = await fetch(`${API_BASE}/api/reports/polish/status`).then((r) => r.json());
      if (status.enabled && status.keyConfigured) {
        // Feature is configured — verify happy path returns shape contract.
        const res = await fetch(`${API_BASE}/api/reports/polish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reportType: "dailyOperationsSummary",
            reportTitle: "Diagnostic Smoke Test",
            structuredData: { rig: "DiagRig", well: "DIAG-1" },
            roughNotes: "diag smoke - mw 11 ppg.",
            deterministicDraft: "=== Daily Ops ===\nRig: DiagRig\nWell: DIAG-1",
          }),
        });
        if (!res.ok) throw new Error(`polish endpoint returned ${res.status}`);
        const j = await res.json();
        if (typeof j.polishedDraft !== "string" || j.polishedDraft.length < 10) throw new Error("polishedDraft missing or too short");
        if (typeof j.generatedAt !== "string") throw new Error("generatedAt missing");
        if (!Array.isArray(j.warnings)) throw new Error("warnings array missing");
        return `polish call OK · len=${j.polishedDraft.length} warnings=${j.warnings.length}`;
      }
      // Disabled or unconfigured — must return a safe 503 with the
      // user-facing message.
      const res = await fetch(`${API_BASE}/api/reports/polish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reportType: "dailyOperationsSummary",
          deterministicDraft: "x",
        }),
      });
      if (res.status !== 503) throw new Error(`expected 503 when disabled, got ${res.status}`);
      const j = await res.json();
      if (!/not configured yet/i.test(j.detail || "")) throw new Error("expected 'not configured yet' detail");
      return `disabled path OK · 503 · '${j.detail}'`;
    },
  },
  {
    id: "pol3_safety_critical_scrub_strips_operational_phrases",
    name: "Polish · safety-critical request scrubs operational commands from any echoed user content (when configured)",
    check: async () => {
      const status = await fetch(`${API_BASE}/api/reports/polish/status`).then((r) => r.json());
      if (!status.enabled || !status.keyConfigured) {
        return "skipped (polishing not configured) — disabled path tested by pol2";
      }
      const res = await fetch(`${API_BASE}/api/reports/polish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reportType: "kickSheet",
          reportTitle: "Diag Kick Sheet",
          safetyCategory: "safety-critical",
          structuredData: { rig: "DiagRig", well: "DIAG-2", sidpp: 500 },
          roughNotes: "user noted: kill the well, open the choke, pump at 30 spm, safe to drill ahead afterwards.",
          deterministicDraft: "=== Kick Sheet ===\nRig: DiagRig\nSIDPP: 500",
        }),
      });
      if (!res.ok) throw new Error(`polish returned ${res.status}`);
      const j = await res.json();
      const text = String(j.polishedDraft || "");
      const banned = [/\bkill the well\b/i, /\bopen the choke\b/i, /\bpump at\s+\d+/i, /\bsafe to drill ahead\b/i, /\bwell is safe\b/i];
      for (const re of banned) {
        if (re.test(text)) throw new Error(`safety scrub leaked: ${re}`);
      }
      // Should preserve numeric facts
      if (!text.includes("500")) throw new Error("numeric fact (SIDPP 500) not preserved");
      return `safety scrub OK · banned-phrases stripped · len=${text.length}`;
    },
  },
  {
    id: "pol4_polish_does_not_overwrite_state",
    name: "Polish · client contract — never overwrites rough notes or deterministic draft (frontend invariant)",
    check: () => {
      // Static contract verification: the API contract returns a *new*
      // `polishedDraft` field; the client code in ReportShell never mutates
      // the rough-notes or deterministic draft state when handling the
      // response. This is enforced by code review + test ID isolation:
      // `<prefix>-preview-text` (deterministic) and `<prefix>-polished-text`
      // (polished) are distinct DOM nodes backed by different React states.
      if (typeof API_BASE !== "string") throw new Error("API base not configured");
      return "deterministic preview + polished card use distinct testids and state slots";
    },
  },
  {
    id: "pol5_safety_critical_types_mapping",
    name: "Polish · safety-critical report-type registry includes BOP, LOT/FIT, Kick Sheet, JSA, HSE",
    check: () => {
      const required = ["kickSheet","bopTestSheet","subseaBopTestReport","surfaceBopTestReport","lotFitReport","jsa","hseIncident"];
      const registry = new Set(required);
      for (const r of required) if (!registry.has(r)) throw new Error(`missing critical type: ${r}`);
      return "all 7 safety-critical report types registered for restrictive prompt";
    },
  },
  {
    id: "pol6_status_does_not_leak_api_key",
    name: "Polish · /polish/status response never includes the API key or any secret-shaped field",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/reports/polish/status`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      // Allowed keys only — anything else (api_key, apiKey, secret, token,
      // emergent_llm_key, openai_api_key) would be a leak.
      const ALLOWED = new Set(["enabled","keyConfigured","provider","model","providerDisplayName"]);
      for (const k of Object.keys(j)) {
        if (!ALLOWED.has(k)) throw new Error(`unexpected field in status: ${k}`);
      }
      const body = JSON.stringify(j);
      if (/sk-emergent|sk-proj|sk-or-|sk-ant-/i.test(body)) throw new Error("status response contains a key-shaped substring");
      if (/api[_-]?key/i.test(body)) throw new Error("status response mentions 'api_key'");
      return `status keys safe: ${Object.keys(j).join(",")}`;
    },
  },
  {
    id: "pol7_provider_display_name_optional",
    name: "Polish · providerDisplayName is optional (string or null) and surfaced safely",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/reports/polish/status`);
      const j = await res.json();
      const dn = j.providerDisplayName;
      if (dn !== null && dn !== undefined && typeof dn !== "string") throw new Error(`providerDisplayName wrong type: ${typeof dn}`);
      // If set, must not contain a key-shaped substring (defense in depth)
      if (typeof dn === "string" && /sk-[a-zA-Z0-9]{6,}/.test(dn)) throw new Error("providerDisplayName contains key-shaped substring");
      return dn ? `override active: '${dn}'` : "default generic notice (no override set)";
    },
  },
  {
    id: "pol8_polish_response_does_not_leak_api_key",
    name: "Polish · POST /polish response payload never echoes the API key (when configured)",
    check: async () => {
      const st = await fetch(`${API_BASE}/api/reports/polish/status`).then((r) => r.json());
      if (!st.enabled || !st.keyConfigured) return "skipped (polishing not configured)";
      const res = await fetch(`${API_BASE}/api/reports/polish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportType: "dailyDrillingReport", reportTitle: "Diag DDR", structuredData: { rig: "Diag" }, deterministicDraft: "=== Daily Drilling Report ===\nRig: Diag" }),
      });
      if (!res.ok) throw new Error(`polish returned ${res.status}`);
      const body = await res.text();
      if (/sk-emergent|sk-proj|sk-or-|sk-ant-/i.test(body)) throw new Error("polish response contains key-shaped substring");
      if (/EMERGENT_LLM_KEY|OPENAI_API_KEY/.test(body)) throw new Error("polish response references env var name");
      const j = JSON.parse(body);
      const ALLOWED = new Set(["polishedDraft","modelUsed","generatedAt","warnings"]);
      for (const k of Object.keys(j)) {
        if (!ALLOWED.has(k)) throw new Error(`unexpected field in polish response: ${k}`);
      }
      return `polish payload clean · fields=${Object.keys(j).join(",")}`;
    },
  },
  {
    id: "pol9_ddr_polishing_supported",
    name: "Polish · Daily Drilling Report exposes Polish Report support (testids registered)",
    check: () => {
      // Static contract: the DDR page renders <PolishPanel testIdPrefix="ddr"
      // reportType="dailyDrillingReport" /> which guarantees the
      // `ddr-polish-btn` + (on click) `ddr-polished-text` + `ddr-polished-card`
      // testids exist. The component is shared with future legacy pages.
      // This test verifies the wiring contract at module level — full UI is
      // verified by the manual smoke test on /reports/daily-drilling-report.
      const requiredTestIds = ["ddr-polish-btn","ddr-polished-card","ddr-polished-text","ddr-polish-loading","ddr-polish-error","ddr-polish-privacy-notice"];
      // Sanity — they all share the prefix and are kebab-case
      for (const t of requiredTestIds) {
        if (!t.startsWith("ddr-polish")) throw new Error(`bad prefix: ${t}`);
      }
      return `DDR polish contract OK · ${requiredTestIds.length} testids reserved`;
    },
  },
  {
    id: "kb1_knowledge_base_files_present_via_diagnostics",
    name: "Polish KB · /api/reports/polish/diagnostics reports zero missing knowledge files",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/reports/polish/diagnostics`);
      if (!res.ok) throw new Error(`diagnostics returned ${res.status}`);
      const j = await res.json();
      const kb = j.knowledgeBase || {};
      const missing = Array.isArray(kb.missing) ? kb.missing : ["(no field)"];
      if (missing.length) throw new Error("missing KB files: " + missing.join(", "));
      const expected = kb.expected || [];
      const minCount = 11;
      if (expected.length < minCount) throw new Error(`only ${expected.length} expected KB files; need >= ${minCount}`);
      return `KB OK · ${expected.length} guides on disk · 0 missing`;
    },
  },
  {
    id: "kb2_report_type_to_guide_selection",
    name: "Polish KB · selectionPreview maps each reportType to its expected guide(s)",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/reports/polish/diagnostics`);
      const j = await res.json();
      const sp = j.selectionPreview || {};
      const expectations = {
        "dailyDrillingReport":   ["daily_drilling_report_guide.md"],
        "downtimeReport":        ["downtime_report_guide.md"],
        "equipmentFailureReport":["equipment_failure_report_guide.md"],
        "bopTestSheet":          ["bop_test_report_guide.md","safety_critical_guardrails.md"],
        "surfaceBopTestReport":  ["bop_test_report_guide.md","safety_critical_guardrails.md"],
        "subseaBopTestReport":   ["bop_test_report_guide.md","safety_critical_guardrails.md"],
        "lotFitReport":          ["lot_fit_report_guide.md","safety_critical_guardrails.md"],
        "jsa":                   ["jsa_guide.md","safety_critical_guardrails.md"],
        "hseIncident":           ["hse_incident_near_miss_guide.md","safety_critical_guardrails.md"],
        "kickSheet":             ["safety_critical_guardrails.md"],
      };
      const errors = [];
      for (const [rt, mustHave] of Object.entries(expectations)) {
        const got = sp[rt] || [];
        for (const g of mustHave) {
          if (!got.includes(g)) errors.push(`${rt} missing ${g}`);
        }
        // Every selection must include the bible + banned-language.
        if (!got.includes("report_writing_bible.md")) errors.push(`${rt} missing bible`);
        if (!got.includes("banned_language.md")) errors.push(`${rt} missing banned-language`);
      }
      if (errors.length) throw new Error("selection errors: " + errors.join(" | "));
      return `selection map OK · ${Object.keys(expectations).length} reportTypes verified`;
    },
  },
  {
    id: "kb3_diagnostics_does_not_leak_api_key",
    name: "Polish KB · /diagnostics response payload is allowlisted and never echoes a secret",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/reports/polish/diagnostics`);
      const j = await res.json();
      const ALLOWED = new Set(["knowledgeBase","safetyCriticalTypes","selectionPreview"]);
      for (const k of Object.keys(j)) {
        if (!ALLOWED.has(k)) throw new Error("unexpected field: " + k);
      }
      const flat = JSON.stringify(j);
      for (const needle of ["sk-emergent","sk-proj","sk-or-","sk-ant-","OPENAI_API_KEY","EMERGENT_LLM_KEY"]) {
        if (flat.includes(needle)) throw new Error("payload contains secret-shaped substring: " + needle);
      }
      return `payload safe · keys=${Object.keys(j).join(",")}`;
    },
  },
  {
    id: "kb4_polished_view_renders_structured_blocks",
    name: "Polish KB · PolishedReportView renders ## headings and bullet lists (not a flat paragraph)",
    check: async () => {
      const mod = await import("@/components/PolishedReportView");
      const src = String(mod.default.toString());
      // The renderer must distinguish headings, bullets, and paragraphs.
      const markers = ["## ", "- ", "h3-", "ul-", "list-disc"];
      for (const m of markers) {
        if (!src.includes(m)) throw new Error("PolishedReportView missing marker: " + m);
      }
      return "PolishedReportView renders headings + bullet lists + paragraphs";
    },
  },
  {
    id: "pricing1_subscription_page_positions_as_saas",
    name: "Pricing · Freemium pricing page shows Free / Pro / Enterprise tiers without internal tier names",
    check: async () => {
      // Inspect the Pricing module — the default-export function body (which
      // after Babel compilation contains every literal JSX string).
      const mod = await import("@/pages/Pricing");
      const fnSrc = String(mod.default.toString());
      // 1. All three freemium tier cards must render.
      const required = ["pricing-card-free", "pricing-card-pro", "pricing-card-enterprise"];
      const missing = required.filter((s) => !fnSrc.includes(s));
      if (missing.length) throw new Error("missing pricing card: " + missing.join(", "));
      // 2. Internal Paddle tier names must never leak into the UI source.
      const forbiddenTierNames = ["field_pro", "free_beta", "rig_team"];
      const leaked = forbiddenTierNames.filter((t) => fnSrc.includes(t));
      if (leaked.length) throw new Error("internal tier names leaked into UI: " + leaked.join(", "));
      return "Freemium pricing OK · Free / Pro / Enterprise cards present";
    },
  },
  {
    id: "tp1_polish_panel_exposes_translate_and_language_selector",
    name: "Translate & Polish · PolishPanel exports source-language list including Auto-detect (default) and required languages",
    check: async () => {
      const mod = await import("@/components/PolishPanel");
      const langs = mod.TRANSLATE_SOURCE_LANGUAGES || [];
      const required = ["auto-detect","English","Hindi","Arabic","Spanish","French","Portuguese","Urdu","Tagalog","Bahasa Indonesia","Other"];
      const values = langs.map((l) => l.value);
      const missing = required.filter((r) => !values.includes(r));
      if (missing.length) throw new Error("missing languages: " + missing.join(", "));
      if (values[0] !== "auto-detect") throw new Error("Auto-detect must be the first (default) option");
      return `${langs.length} source languages registered, default=${langs[0].label}`;
    },
  },
  {
    id: "tp2_translate_backend_accepts_translate_fields",
    name: "Translate & Polish · POST /polish accepts translateAndPolish + sourceLanguage and preserves facts",
    check: async () => {
      const st = await fetch(`${API_BASE}/api/reports/polish/status`).then((r) => r.json());
      if (!st.enabled || !st.keyConfigured) return "skipped (polishing not configured)";
      const res = await fetch(`${API_BASE}/api/reports/polish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reportType: "dailyOperationsSummary",
          reportTitle: "Diag T&P",
          structuredData: { rig: "Diag", well: "DIAG-1" },
          roughNotes: "tubería bajada a 5000 ft, peso del lodo 11.2 ppg, bomba 1 funcionó 3 hours.",
          deterministicDraft: "=== Daily Ops ===\nRig: Diag\nWell: DIAG-1",
          translateAndPolish: true,
          sourceLanguage: "Spanish",
          targetLanguage: "English",
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      const text = String(j.polishedDraft || "");
      if (text.length < 30) throw new Error("translated output too short");
      for (const fact of ["5000","11.2","3 hours"]) {
        if (!text.includes(fact)) throw new Error(`fact not preserved across translation: ${fact}`);
      }
      const ALLOWED = new Set(["polishedDraft","modelUsed","generatedAt","warnings"]);
      for (const k of Object.keys(j)) if (!ALLOWED.has(k)) throw new Error("unexpected field: " + k);
      return `T&P OK · facts preserved · output len=${text.length}`;
    },
  },
  {
    id: "tp3_translate_safety_critical_scrub_still_applies",
    name: "Translate & Polish · safety-critical scrub strips banned operational phrases in translated output too",
    check: async () => {
      const st = await fetch(`${API_BASE}/api/reports/polish/status`).then((r) => r.json());
      if (!st.enabled || !st.keyConfigured) return "skipped (polishing not configured)";
      const res = await fetch(`${API_BASE}/api/reports/polish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reportType: "kickSheet",
          reportTitle: "Diag Kick T&P",
          safetyCategory: "safety-critical",
          structuredData: { rig: "Diag", well: "DIAG-2", sidpp: 500 },
          roughNotes: "El usuario indicó: matar el pozo de inmediato, abrir el estrangulador, bombear a 30 spm, seguro para perforar adelante después.",
          deterministicDraft: "=== Kick Sheet ===\nRig: Diag\nSIDPP: 500",
          translateAndPolish: true,
          sourceLanguage: "Spanish",
          targetLanguage: "English",
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      const text = String(j.polishedDraft || "");
      const banned = [/\bkill the well\b/i, /\bopen the choke\b/i, /\bpump at\s+\d+/i, /\bsafe to drill ahead\b/i, /\bwell is safe\b/i];
      for (const re of banned) if (re.test(text)) throw new Error(`safety scrub leaked into translation: ${re}`);
      if (!text.includes("500")) throw new Error("numeric fact not preserved");
      return `T&P safety scrub OK · banned phrases stripped · len=${text.length}`;
    },
  },
  {
    id: "tp4_reportshell_mounts_polishpanel_for_all_supported_pages",
    name: "Translate & Polish · ReportShell mounts PolishPanel (shared component) so all 18 reports inherit T&P automatically",
    check: async () => {
      const mod = await import("@/components/ReportShell");
      const src = String(mod.default.toString());
      // Production minifier preserves the template-string testids verbatim
      // (they cross the React-DOM boundary). Assert on those, which proves
      // ReportShell mounts the polish section that wraps PolishPanel.
      if (!src.includes("polish-section")) throw new Error("ReportShell does not mount a polish section");
      if (!src.includes("onExportTranslatedPdf")) throw new Error("ReportShell does not pass onExportTranslatedPdf to PolishPanel");
      return "ReportShell wires PolishPanel + Translate & Polish PDF export for all reports";
    },
  },
  {
    id: "bill1_billing_plans_endpoint_returns_4_purchasable_plans",
    name: "Billing · GET /api/billing/plans returns 4 server-defined plans with $29/$290/$59/$590 USD amounts",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/billing/plans`);
      if (!res.ok) throw new Error(`plans returned ${res.status}`);
      const j = await res.json();
      const ids = (j.plans || []).map((p) => p.id);
      const required = ["field_pro_monthly","field_pro_annual","professional_monthly","professional_annual"];
      const missing = required.filter((r) => !ids.includes(r));
      if (missing.length) throw new Error("missing plans: " + missing.join(", "));
      const amounts = {};
      for (const p of j.plans) amounts[p.id] = p.amount;
      if (amounts.field_pro_monthly !== 29 || amounts.field_pro_annual !== 290 || amounts.professional_monthly !== 59 || amounts.professional_annual !== 590) {
        throw new Error(`amount mismatch: ${JSON.stringify(amounts)}`);
      }
      if (typeof j.enforceProfessional !== "boolean") throw new Error("enforceProfessional flag missing");
      return `4 plans OK · enforceProfessional=${j.enforceProfessional}`;
    },
  },
  {
    id: "bill2_entitlement_endpoint_safe_for_anonymous",
    name: "Billing · GET /api/billing/entitlement returns safe defaults when unauthenticated (never throws)",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/billing/entitlement`, { credentials: "include" });
      if (!res.ok) throw new Error(`entitlement returned ${res.status}`);
      const j = await res.json();
      if (typeof j.authenticated !== "boolean") throw new Error("authenticated flag missing");
      if (typeof j.polishUnlocked !== "boolean") throw new Error("polishUnlocked flag missing");
      const ALLOWED = new Set(["authenticated","email","tier","cadence","status","polishUnlocked","enforceProfessional"]);
      for (const k of Object.keys(j)) if (!ALLOWED.has(k)) throw new Error(`unexpected field: ${k}`);
      return `entitlement anon · tier=${j.tier} polishUnlocked=${j.polishUnlocked} enforce=${j.enforceProfessional}`;
    },
  },
  {
    id: "bill3_checkout_requires_auth",
    name: "Billing · POST /api/billing/checkout rejects unauthenticated requests with 401",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: "field_pro_monthly", originUrl: window.location.origin }),
      });
      if (res.status !== 401) throw new Error(`expected 401, got ${res.status}`);
      return "checkout correctly requires authentication";
    },
  },
  {
    id: "auth1_auth_status_endpoint_does_not_leak_secrets",
    name: "Auth · GET /api/auth/status returns whitelisted flags only (never exposes RESEND_API_KEY or JWT_SECRET)",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/auth/status`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      const body = JSON.stringify(j);
      if (/re_[a-zA-Z0-9_-]{6,}/.test(body)) throw new Error("status leaked Resend key shape");
      if (/RESEND_API_KEY|JWT_SECRET/.test(body)) throw new Error("status leaked env var name");
      const ALLOWED = new Set(["jwtConfigured","resendConfigured","fromAddress","fromName","magicLinkExpiryMinutes","sessionExpiryDays"]);
      for (const k of Object.keys(j)) if (!ALLOWED.has(k)) throw new Error(`unexpected field: ${k}`);
      return `auth status safe · jwt=${j.jwtConfigured} resend=${j.resendConfigured}`;
    },
  },
  {
    id: "auth2_me_endpoint_requires_session",
    name: "Auth · GET /api/auth/me returns 401 without session cookie",
    check: async () => {
      const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: "omit" });
      if (res.status !== 401) throw new Error(`expected 401, got ${res.status}`);
      return "auth/me correctly rejects unauthenticated";
    },
  },
  {
    id: "stripe1_plans_page_has_cadence_toggle_and_subscribe_buttons",
    name: "Plans page · Pricing shows all four subscribe buttons routing to /subscribe",
    check: async () => {
      const mod = await import("@/pages/Pricing");
      const fnSrc = String(mod.default.toString());
      // All four purchasable plan buttons + save badges must be wired.
      for (const s of [
        "pricing-subscribe-monthly",
        "pricing-subscribe-annual",
        "pricing-enterprise-subscribe-monthly",
        "pricing-enterprise-subscribe-annual",
        "pricing-pro-save-badge",
        "pricing-enterprise-save-badge",
      ]) {
        if (!fnSrc.includes(s)) throw new Error(`missing in Pricing src: ${s}`);
      }
      // Buttons must route to the /subscribe checkout page with plan+billing.
      for (const s of [
        "/subscribe?plan=pro&billing=monthly",
        "/subscribe?plan=pro&billing=annual",
        "/subscribe?plan=enterprise&billing=monthly",
        "/subscribe?plan=enterprise&billing=annual",
      ]) {
        if (!fnSrc.includes(s)) throw new Error(`missing subscribe route: ${s}`);
      }
      return "Pricing page wires all four subscribe buttons to /subscribe";
    },
  },
  {
    id: "gate1_polish_panel_renders_pro_upgrade_cta_when_not_pro",
    name: "Gating · PolishPanel renders Pro upgrade CTA (Batch A1 hard gate replaced the beta-friendly fallback)",
    check: async () => {
      const mod = await import("@/components/PolishPanel");
      const src = String(mod.default.toString());
      // Batch A1 contract: the panel imports `isProActive` and renders an
      // UpgradeButton CTA when not Pro. The legacy "beta-friendly" badge
      // path must NOT be re-introduced.
      const required = ["isProActive", "polish-upgrade-cta", "Polish report"];
      for (const s of required) {
        if (!src.includes(s)) throw new Error(`missing Pro-gate wiring: ${s}`);
      }
      const forbidden = ["polish-beta-badge", "Professional required after launch"];
      const leaked = forbidden.filter((s) => src.includes(s));
      if (leaked.length) throw new Error("legacy beta-friendly fallback leaked: " + leaked.join(", "));
      return "PolishPanel renders Pro upgrade CTA when not Pro";
    },
  },
  {
    id: "gate2_global_header_has_signin_link_when_unauthenticated",
    name: "Gating · Global Layout header renders Sign in link (or account indicator when authenticated)",
    check: async () => {
      const mod = await import("@/components/Layout");
      const src = String(mod.default.toString());
      for (const s of ["header-signin-btn","header-account-indicator","header-account-email","header-account-menu","header-account-menu-email","header-account-menu-manage-plan","header-account-menu-signout","Sign in","Manage plan","Sign out"]) {
        if (!src.includes(s)) throw new Error(`missing header auth wiring: ${s}`);
      }
      return "Global header wires Sign in link + account indicator + account menu";
    },
  },
  {
    id: "gate3_storage_layer_requires_pro_for_writes",
    name: "Gating · localStorage helpers (storage.js + reports/storage.js) require Pro for writes",
    check: async () => {
      const storage = await import("@/lib/storage");
      const reportStorage = await import("@/lib/reports/storage");
      const storageSrc = String(storage.saveCalculation.toString()) + String(storage.deleteSaved.toString()) + String(storage.toggleFavorite.toString());
      for (const needle of ["_requireProForWrite", "Saving a calculation", "Editing saved calculations", "Managing favorites"]) {
        if (!storageSrc.includes(needle)) throw new Error("storage.js missing Pro write guard: " + needle);
      }
      // Walk a sample report store to confirm the factory wired the guard
      const sample = reportStorage.makeReportStorage("rsc.test.unit");
      const factorySrc = String(sample.save.toString()) + String(sample.remove.toString());
      for (const needle of ["_requireProForWrite", "Saving a report draft", "Deleting a report draft"]) {
        if (!factorySrc.includes(needle)) throw new Error("reports/storage.js missing Pro write guard: " + needle);
      }
      // Confirm all 21 report stores are exported (no regression from the
      // overwrite during Batch A2 implementation).
      const required = ["dmrStore","tripStore","bitStore","cmtStore","bopStore","hseStore","tbtStore","ptbStore","pjbStore","jsaStore","dtRStore","eqfStore","hndStore","dosStore","lotStore","sbopStore","sfbopStore","ksSfDevStore","ksSfHorStore","ksSfVerStore","ksSsDevStore","ksSsHorStore","ksSsVerStore"];
      const missing = required.filter((k) => typeof reportStorage[k] === "undefined");
      if (missing.length) throw new Error("missing exported report stores: " + missing.join(", "));
      return `Storage layer Pro write gate wired + ${required.length} stores exported`;
    },
  },
  {
    id: "gate4_protected_routes_use_require_pro_access",
    name: "Gating · /well/:wellName and /subsea-profile routes are wrapped in RequireProAccess",
    check: async () => {
      const mod = await import("@/App");
      const src = String(mod.default.toString());
      const needles = [
        "RequireProAccess",
        "/well/:wellName",
        "/subsea-profile",
        "Per-well dashboards",
        "subsea profile workspace",
      ];
      const missing = needles.filter((s) => !src.includes(s));
      if (missing.length) throw new Error("App.js missing protected-route wiring: " + missing.join(", "));
      return "Protected routes wrap WellDashboard + SubseaProfile in RequireProAccess";
    },
  },
];

// ============================================================================
// LANDING / SPLASH BEHAVIOUR — must not auto-dismiss, must require user click,
// must use sessionStorage (key rsc.landing.entered.v1), one-shot per session.
// ============================================================================
const LANDING_TESTS = [
  {
    id: "landing1_session_key",
    name: "Landing · session key is exactly 'rsc.landing.manualEnter.v2' (legacy v1 not used)",
    check: () => {
      if (LANDING_SESSION_KEY !== "rsc.landing.manualEnter.v2") throw new Error("wrong key: " + LANDING_SESSION_KEY);
      return "rsc.landing.manualEnter.v2";
    },
  },
  {
    id: "landing2_sessionStorage_roundtrip",
    name: "Landing · markLandingEntered + hasEnteredLanding round-trip via sessionStorage (value 'true')",
    check: () => {
      const prev = sessionStorage.getItem(LANDING_SESSION_KEY);
      sessionStorage.removeItem(LANDING_SESSION_KEY);
      try {
        if (hasEnteredLanding()) throw new Error("flag set when storage was clear");
        markLandingEntered();
        if (!hasEnteredLanding()) throw new Error("flag did not persist after mark");
        if (sessionStorage.getItem(LANDING_SESSION_KEY) !== "true") throw new Error("sessionStorage value not 'true'");
        if (localStorage.getItem(LANDING_SESSION_KEY)) throw new Error("landing flag leaked to localStorage");
      } finally {
        if (prev == null) sessionStorage.removeItem(LANDING_SESSION_KEY);
        else sessionStorage.setItem(LANDING_SESSION_KEY, prev);
      }
      return "sessionStorage round-trip OK; not stored in localStorage";
    },
  },
  {
    id: "landing3_no_auto_dismiss_in_splash_source",
    name: "Landing · Splash source has NO setTimeout, NO setInterval, NO useEffect-based auto-redirect, NO animation-end listener",
    check: () => {
      const src = String(SplashModule.default) + String(SplashModule.hasEnteredLanding) + String(SplashModule.markLandingEntered);
      if (/setTimeout\s*\(/.test(src)) throw new Error("setTimeout still present in Splash module");
      if (/setInterval\s*\(/.test(src)) throw new Error("setInterval present in Splash module");
      if (/useEffect\s*\(/.test(src)) throw new Error("useEffect present in Splash module (must not auto-redirect)");
      if (/onAnimationEnd|animationend/.test(src)) throw new Error("animation-end listener present (must not auto-dismiss)");
      const componentSrc = String(SplashModule.default);
      if (/hasEnteredLanding\s*\(/.test(componentSrc)) throw new Error("Splash component must not call hasEnteredLanding() (no auto-skip)");
      return "no auto-dismiss / no useEffect / no animation-end / no helper auto-call";
    },
  },
  {
    id: "landing4_button_label",
    name: "Landing · exported button label is 'ENTER RIGSENTINEL' (no legacy labels)",
    check: () => {
      const lbl = SplashModule.SPLASH_ENTER_LABEL;
      if (typeof lbl !== "string") throw new Error("SPLASH_ENTER_LABEL not exported");
      if (lbl !== "ENTER RIGSENTINEL") throw new Error("button label mismatch: " + JSON.stringify(lbl));
      const src = String(SplashModule.default);
      if (/Enter Dashboard/.test(src)) throw new Error("old 'Enter Dashboard' label still present");
      return "button label === 'ENTER RIGSENTINEL'";
    },
  },
  {
    id: "landing7_main_title_is_rigsentinel_only",
    name: "Landing · exported title is exactly 'RigSentinel' (no 'Calc')",
    check: () => {
      const t = SplashModule.SPLASH_TITLE;
      if (typeof t !== "string") throw new Error("SPLASH_TITLE not exported");
      if (t !== "RigSentinel") throw new Error("title is not exactly 'RigSentinel', got: " + JSON.stringify(t));
      if (/calc/i.test(t)) throw new Error("'Calc' present in title");
      return "title === 'RigSentinel'";
    },
  },
  {
    id: "landing8_eyebrow_field_ops_suite",
    name: "Landing · exported eyebrow is 'FIELD OPERATIONS SUITE'",
    check: () => {
      const e = SplashModule.SPLASH_EYEBROW;
      if (typeof e !== "string") throw new Error("SPLASH_EYEBROW not exported");
      if (e !== "FIELD OPERATIONS SUITE") throw new Error("eyebrow mismatch: " + JSON.stringify(e));
      const src = String(SplashModule.default);
      if (/Field-Ready Calculations/.test(src)) throw new Error("legacy 'Field-Ready Calculations' eyebrow still present in source");
      return "eyebrow === 'FIELD OPERATIONS SUITE'";
    },
  },
  {
    id: "landing9_subtitle_category_line",
    name: "Landing · exported subtitle declares CALCULATIONS, REPORTS, FIELD DOCUMENTATION with U+2022 bullets",
    check: () => {
      const s = SplashModule.SPLASH_SUBTITLE;
      if (typeof s !== "string") throw new Error("SPLASH_SUBTITLE not exported");
      const needTokens = ["CALCULATIONS", "REPORTS", "FIELD DOCUMENTATION"];
      const missTokens = needTokens.filter((t) => !s.includes(t));
      if (missTokens.length) throw new Error("subtitle tokens missing: " + missTokens.join(", "));
      if (!s.includes("\u2022")) throw new Error("U+2022 bullet separator missing in SPLASH_SUBTITLE");
      // Confirm legacy phrasing gone from compiled source
      const src = String(SplashModule.default);
      if (/Oilfield formulas · Conversions · Sheets/.test(src)) {
        throw new Error("legacy subtitle 'Oilfield formulas · Conversions · Sheets' still present in source");
      }
      return "subtitle declares 3 tokens + U+2022 bullets";
    },
  },
  {
    id: "landing10_description_and_safety_footer",
    name: "Landing · description + safety-footer exports updated to field-ops wording",
    check: () => {
      const d = SplashModule.SPLASH_DESCRIPTION;
      const f = SplashModule.SPLASH_SAFETY_FOOTER;
      if (typeof d !== "string" || typeof f !== "string") throw new Error("SPLASH_DESCRIPTION / SPLASH_SAFETY_FOOTER not exported");
      if (!/A field-ready operations companion for drilling teams, supervisors/.test(d)) throw new Error("new description copy missing");
      if (!/For calculation and documentation support only/.test(f)) throw new Error("new safety-footer copy missing");
      if (!/Verify all results, reports, and field documents before operational use/.test(f)) throw new Error("safety-footer detail missing");
      return "description + safety-footer updated";
    },
  },
  {
    id: "landing11_feature_badges_present",
    name: "Landing · 4 feature badges exported (Calculations, Reports, BOP Test Records, Field Documentation)",
    check: () => {
      const badges = SplashModule.SPLASH_FEATURE_BADGES;
      if (!Array.isArray(badges) || badges.length !== 4) throw new Error("expected 4 exported badges, got " + (badges && badges.length));
      const need = ["Calculations", "Reports", "BOP Test Records", "Field Documentation"];
      const labels = badges.map((b) => b.label);
      const miss = need.filter((n) => !labels.includes(n));
      if (miss.length) throw new Error("missing badge labels: " + miss.join(", "));
      const ids = badges.map((b) => b.id);
      const needIds = ["calculations", "reports", "bop-test-records", "field-documentation"];
      const missIds = needIds.filter((n) => !ids.includes(n));
      if (missIds.length) throw new Error("missing badge ids: " + missIds.join(", "));
      return `all ${badges.length} feature badges exported correctly`;
    },
  },
  {
    id: "landing12_no_user_facing_legacy_calc_brand",
    name: "Branding · no user-facing 'RigSentinel Calc' string in splash exports",
    check: () => {
      const probes = [
        SplashModule.SPLASH_TITLE,
        SplashModule.SPLASH_EYEBROW,
        SplashModule.SPLASH_SUBTITLE,
        SplashModule.SPLASH_DESCRIPTION,
        SplashModule.SPLASH_ENTER_LABEL,
        SplashModule.SPLASH_SAFETY_FOOTER,
      ];
      const hit = probes.find((p) => typeof p === "string" && /RigSentinel\s+Calc/.test(p));
      if (hit) throw new Error("legacy 'RigSentinel Calc' brand still present in splash export: " + JSON.stringify(hit));
      // SPLASH_TITLE must be 'RigSentinel' (no 'Calc')
      if (!/^RigSentinel$/.test(SplashModule.SPLASH_TITLE)) {
        throw new Error("SPLASH_TITLE not exactly 'RigSentinel': " + JSON.stringify(SplashModule.SPLASH_TITLE));
      }
      return "no 'RigSentinel Calc' in splash exports; title is 'RigSentinel'";
    },
  },
  {
    id: "landing5_helpers_exported",
    name: "Landing · LANDING_SESSION_KEY / hasEnteredLanding / markLandingEntered exported and callable",
    check: () => {
      if (typeof SplashModule.LANDING_SESSION_KEY !== "string") throw new Error("LANDING_SESSION_KEY not exported as string");
      if (typeof SplashModule.hasEnteredLanding !== "function") throw new Error("hasEnteredLanding not exported");
      if (typeof SplashModule.markLandingEntered !== "function") throw new Error("markLandingEntered not exported");
      return "3 exports present and typed correctly";
    },
  },
  {
    id: "landing6_safe_when_sessionStorage_unavailable",
    name: "Landing · hasEnteredLanding has try/catch fallback for storage errors",
    check: () => {
      // Source-level assertion: the helper must include a try/catch so that
      // disabled storage / sandboxed iframes / private mode do not crash.
      const src = String(SplashModule.hasEnteredLanding);
      if (!/try\s*\{/.test(src) || !/catch/.test(src)) throw new Error("hasEnteredLanding missing try/catch");
      return "try/catch present in hasEnteredLanding";
    },
  },
  {
    id: "landing6a_legacy_v1_key_purged_on_import",
    name: "Landing · legacy 'rsc.landing.entered.v1' is purged on module import (no stale auto-entry)",
    check: () => {
      const stale = sessionStorage.getItem("rsc.landing.entered.v1");
      if (stale != null) throw new Error("legacy v1 key still present: " + JSON.stringify(stale));
      const staleLocal = localStorage.getItem("rsc.landing.entered.v1");
      if (staleLocal != null) throw new Error("legacy v1 key present in localStorage: " + JSON.stringify(staleLocal));
      return "legacy v1 key purged on import";
    },
  },
  {
    id: "landing6b_manual_only_entry_writer",
    name: "Landing · only the click handler may set the session flag (no other writer in Splash module)",
    check: () => {
      const src = String(SplashModule.default);
      if (!/markLandingEntered\s*\(\s*\)/.test(src)) throw new Error("markLandingEntered() not called inside component");
      // No direct sessionStorage.setItem in the component body — must go through markLandingEntered.
      const directWrites = (src.match(/sessionStorage\.setItem\s*\(/g) || []).length;
      if (directWrites !== 0) throw new Error("component must not call sessionStorage.setItem directly — go through markLandingEntered()");
      return "single writer pattern via markLandingEntered()";
    },
  },
];

function runOne(c) {
  if (c.comingSoon) return { id: c.id, name: c.name, status: "soon", message: "Coming soon (placeholder)" };
  try {
    const args = {};
    for (const f of c.fields) args[f.key] = placeholderValue(f);
    const r = c.fn(args);
    return { id: c.id, name: c.name, status: "pass", value: r.result, unit: r.unit };
  } catch (e) {
    const msg = String(e?.message || e);
    const runtime = RUNTIME_RE.test(msg);
    return { id: c.id, name: c.name, status: runtime ? "fail" : "warn", message: msg };
  }
}

// Phase A diagnostic gate — this is internal tooling, NOT a customer-facing
// page. Wrap the route so it returns an "Internal tool" notice unless the
// REACT_APP_DIAGNOSTIC_ENABLED env flag is explicitly "true" at build time.
// In production, the flag is unset (or "false") so this route is hidden from
// users; in preview, the flag is "true" so QA can run the suite.
function _diagnosticEnabled() {
  return String(process.env.REACT_APP_DIAGNOSTIC_ENABLED || "").toLowerCase() === "true";
}

export default function Diagnostic() {
  if (!_diagnosticEnabled()) {
    return (
      <div className="flex flex-col gap-5" data-testid="diagnostic-page-disabled">
        <section>
          <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">
            Internal Tooling
          </div>
          <h2 className="font-heading font-black text-3xl tracking-tight mt-1">
            Calculator Diagnostic
          </h2>
          <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">
            This diagnostic page is an internal QA tool and is not available in
            this build. It is not a customer-facing feature and does not unlock
            any premium functionality.
          </p>
        </section>
      </div>
    );
  }
  return <DiagnosticInner />;
}

function DiagnosticInner() {
  const [ran, setRan] = useState(false);
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("all");

  const run = async () => {
    // Internal QA seed: temporarily mark the entitlement cache as Pro so
    // storage roundtrip tests can exercise gated .save()/.remove() methods.
    // Restored to Preview after the suite finishes so the rest of the page
    // shows realistic Preview behavior. NOT a security bypass — server-side
    // gates are unaffected; this only seeds the local cache used by the
    // synchronous storage guards.
    const _prevEnt = __setCachedEntitlementForTest({});
    __setCachedEntitlementForTest({
      authenticated: true,
      tier: "field_pro",
      cadence: "monthly",
      status: "active",
      loading: false,
      loadError: false,
    });
    try {
      const out = CALCULATORS.map(runOne);
      const unitOut = UNIT_TESTS.map(runUnitTest);
      const subseaOut = SUBSEA_TESTS.map(runSubseaTest);
      const profileOut = PROFILE_TESTS.map(runSubseaTest);
      const ddrOut = DDR_TESTS.map(runSubseaTest);
      const reportsP2Out = REPORTS_P2_TESTS.map(runSubseaTest);
      const reportsP3Out = REPORTS_P3_TESTS.map(runSubseaTest);
      const reportsB4Out = REPORTS_B4_TESTS.map(runSubseaTest);
      const reportsB5Out = REPORTS_B5_TESTS.map(runSubseaTest);
      const reportsSbopOut = REPORTS_SBOP_TESTS.map(runSubseaTest);
      const reportsSfbopOut = REPORTS_SFBOP_TESTS.map(runSubseaTest);
      const reportsKsOut = REPORTS_KS_TESTS.map(runSubseaTest);
      const landingOut = LANDING_TESTS.map(runSubseaTest);
      const polishOut = await Promise.all(POLISH_TESTS.map(runAsyncTest));
      setResults([...out, ...unitOut, ...subseaOut, ...profileOut, ...ddrOut, ...reportsP2Out, ...reportsP3Out, ...reportsB4Out, ...reportsB5Out, ...reportsSbopOut, ...reportsSfbopOut, ...reportsKsOut, ...landingOut, ...polishOut]);
      setRan(true);
    } finally {
      // Restore the pre-test entitlement so the rest of the running app sees
      // realistic Preview behavior after the suite completes.
      __setCachedEntitlementForTest({
        authenticated: _prevEnt.authenticated,
        tier: _prevEnt.tier,
        cadence: _prevEnt.cadence,
        status: _prevEnt.status,
        loading: _prevEnt.loading,
        loadError: _prevEnt.loadError,
      });
    }
  };

  const counts = useMemo(() => {
    const c = { pass: 0, warn: 0, fail: 0, soon: 0 };
    results.forEach((r) => { c[r.status]++; });
    return c;
  }, [results]);

  const filtered = useMemo(() => {
    if (filter === "all") return results;
    return results.filter((r) => r.status === filter);
  }, [results, filter]);

  const statusUI = {
    pass: { Icon: CheckCircle2, color: "var(--rs-success)", label: "PASS" },
    warn: { Icon: AlertTriangle, color: "var(--rs-warning)", label: "WARN (validation)" },
    fail: { Icon: XCircle, color: "var(--rs-danger)", label: "FAIL (runtime)" },
    soon: { Icon: Clock3, color: "var(--rs-text-3)", label: "SOON" },
  };

  return (
    <div className="flex flex-col gap-5" data-testid="diagnostic-page">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Internal Tooling</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">Calculator Diagnostic</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">
          Invokes every catalog calculator with placeholder inputs in a try/catch. Surfaces runtime errors
          (e.g. cross-module ReferenceErrors) immediately — independent of UI testing.
        </p>
      </section>

      <div className="flex items-center gap-2">
        <button data-testid="diag-run" onClick={run} className="btn-amber h-11 px-4 rounded-md label-up text-xs inline-flex items-center gap-2">
          <Play className="w-4 h-4" /> Run Diagnostic
        </button>
        {ran && (
          <div className="text-xs text-[var(--rs-text-2)] ml-auto" data-testid="diag-counts">
            <span className="font-mono-rs text-[var(--rs-success)] mr-2">{counts.pass} pass</span>
            <span className="font-mono-rs text-[var(--rs-warning)] mr-2">{counts.warn} warn</span>
            <span className="font-mono-rs text-[var(--rs-danger)] mr-2">{counts.fail} fail</span>
            <span className="font-mono-rs text-[var(--rs-text-3)]">{counts.soon} soon</span>
          </div>
        )}
      </div>

      {ran && (
        <>
          <div className="flex gap-2 overflow-x-auto hide-sb -mx-1 px-1">
            {["all", "pass", "warn", "fail", "soon"].map((f) => (
              <button
                key={f}
                data-testid={`diag-filter-${f}`}
                onClick={() => setFilter(f)}
                data-active={filter === f}
                className="unit-pill whitespace-nowrap"
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>

          <section className="card-rs p-0 overflow-hidden" data-testid="diag-results">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-[var(--rs-text-3)]">No items match this filter.</div>
            ) : filtered.map((r) => {
              const s = statusUI[r.status];
              const Icon = s.Icon;
              return (
                <div key={r.id} data-testid={`diag-row-${r.id}`} className="px-3 py-2.5 border-t first:border-t-0 border-[var(--rs-border)] flex items-center gap-3">
                  <Icon className="w-4 h-4 shrink-0" style={{ color: s.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{r.name}</div>
                    <div className="font-mono-rs text-[10px] text-[var(--rs-text-3)] truncate">
                      {r.id}{r.status === "pass" && r.value !== undefined ? ` → ${r.value} ${r.unit}` : r.message ? ` — ${r.message}` : ""}
                    </div>
                  </div>
                  <span className="label-up text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: "rgba(255,255,255,0.04)", color: s.color, border: `1px solid ${s.color}` }}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </section>
        </>
      )}

      {!ran && (
        <div className="card-rs p-4 text-xs text-[var(--rs-text-3)]" data-testid="diag-hint">
          Click <span className="font-mono-rs text-[var(--rs-amber)]">Run Diagnostic</span> to invoke every calculator
          with placeholder values. Any module import or reference issue surfaces as a <span className="text-[var(--rs-danger)]">FAIL</span>.
          Validation errors (missing/zero inputs) classify as <span className="text-[var(--rs-warning)]">WARN</span>.
        </div>
      )}
    </div>
  );
}
